import React, { useState } from "react";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";
import LayersIcon from "@mui/icons-material/Layers";
import ScienceIcon from "@mui/icons-material/Science";
import SettingsIcon from "@mui/icons-material/Settings";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import InventoryIcon from "@mui/icons-material/Inventory";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import HourglassFullIcon from "@mui/icons-material/HourglassFull";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";

const MaduracionSection = ({
  maduracionRecords = [],
  destilacionRecords = [],
  granelRecords = [],
  isFormOpen,
  setIsFormOpen,
  formData = {},
  setFormData,
  handleSaveMaduracion,
  handleDeleteMaduracion
}) => {
  const [selectedRecord, setSelectedRecord] = useState(null);
  console.log("maduracionRecords", maduracionRecords);
  const today = new Date().toISOString().split("T")[0];

  // Estado para el modal de salida de maduración
  const [isSalidaModalOpen, setIsSalidaModalOpen] = useState(false);
  const [salidaForm, setSalidaForm] = useState({
    maduracionId: "",
    fechaSalida: today,
    volumenSalida: "",
    merma: "0",
    salidaAlc: "",
    salidaRecipientes: "1",
    nuevoLote: "",
    destino: "Tanque de Mezcal a Granel",
    observaciones: ""
  });

  /**
   * Evaluador NOM-070-SCFI-2016 para determinar la clase y edad según recipiente y tiempo transcurrido.
   * IMPORTANTE: Esta regla se evalúa ÚNICAMENTE cuando ya se cuenta con las dos fechas (fechaInicio y fechaFin).
   * - Recipientes de Madera (Barricas, Roble, Encino, Pipones, Toneles):
   *   * >= 60 días (2 meses) y < 365 días (1 año) -> Reposado
   *   * >= 365 días (1 año o más) -> Añejo
   *   * < 60 días -> Blanco / Joven (no alcanza tiempo mínimo para Reposado)
   * - Recipientes de Vidrio (Garrafones, Damajuanas, etc.):
   *   * >= 365 días (1 año o más) -> Madurado en Vidrio
   *   * < 365 días -> Blanco / Joven (en vidrio requiere mínimo 1 año para Madurado en Vidrio)
   */
  const evaluarNormaClase = (tipoRecipiente, fechaInicio, fechaFin) => {
    if (!fechaInicio || !fechaFin || String(fechaFin).trim() === "") {
      return {
        clase: "En Maduración",
        edad: "En proceso (sin salida)",
        dias: 0,
        evaluada: false,
        mensaje: "El lote se encuentra actualmente en maduración. La clase se dictaminará al registrar la fecha de salida."
      };
    }

    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);
    const diffTime = fin.getTime() - inicio.getTime();

    if (isNaN(diffTime) || diffTime < 0) {
      return {
        clase: "Sin definir",
        edad: "Fechas inválidas",
        dias: 0,
        evaluada: false,
        mensaje: "La fecha de salida no puede ser anterior a la fecha de inicio."
      };
    }

    const diffDays = Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)));
    const months = Math.floor(diffDays / 30.4375);
    const years = (diffDays / 365.25).toFixed(1);

    const tipoNorm = String(tipoRecipiente || "").toUpperCase();
    const isVidrio = tipoNorm.includes("VIDRIO") ||
      tipoNorm.includes("GARRAF") ||
      tipoNorm.includes("DAMAJUANA") ||
      tipoNorm.includes("CRISTAL");

    if (isVidrio) {
      if (diffDays >= 365) {
        return {
          clase: "Madurado en Vidrio",
          edad: `${years} año(s) (${diffDays} días)`,
          dias: diffDays,
          evaluada: true,
          mensaje: `Cumple NOM-070 para Madurado en Vidrio (${diffDays} días en recipiente de vidrio).`
        };
      } else {
        return {
          clase: "Blanco / Joven",
          edad: `${diffDays} día(s)`,
          dias: diffDays,
          evaluada: true,
          mensaje: `Tiempo en vidrio (${diffDays} días) menor a 1 año. No alcanza Madurado en Vidrio, conserva clase Blanco/Joven.`
        };
      }
    } else {
      // Madera (Barricas, Pipones, Toneles, Roble, etc.)
      if (diffDays >= 365) {
        return {
          clase: "Añejo",
          edad: `${years} año(s) (${diffDays} días)`,
          dias: diffDays,
          evaluada: true,
          mensaje: `Cumple NOM-070 para Añejo (${diffDays} días en barrica de madera).`
        };
      } else if (diffDays >= 60) {
        return {
          clase: "Reposado",
          edad: `${months} mes(es) (${diffDays} días)`,
          dias: diffDays,
          evaluada: true,
          mensaje: `Cumple NOM-070 para Reposado (${diffDays} días en barrica de madera).`
        };
      } else {
        return {
          clase: "Blanco / Joven",
          edad: `${diffDays} día(s)`,
          dias: diffDays,
          evaluada: true,
          mensaje: `Tiempo en madera (${diffDays} días) menor a 2 meses (60 días). Conserva clase Blanco/Joven.`
        };
      }
    }
  };

  // Recipientes de maduración del formulario
  const recipientes = formData?.recipientes && formData.recipientes.length > 0
    ? formData.recipientes
    : [{
      id: Date.now(),
      tipoRecipiente: "BARRICAS DE MADERA DE",
      detalleRecipiente: "",
      noRecipientes: "1",
      invInicialFecha: formData?.fechaMovimiento || today,
      invInicialRecipientes: "1",
      invInicialVolumen: formData?.invInicialVolumen || "",
      invInicialAlc: formData?.granelAlcGrad || "",
      entradaFolio: "",
      entradaRecipientes: "0",
      entradaVolumen: "0",
      entradaAlc: "0",
      salidaFecha: "",
      salidaRecipientes: "0",
      salidaVolumen: "",
      salidaAlc: "",
      salidaMerma: "",
      nuevoLote: "",
      nuevo_lote: "",
      invFinalRecipientes: "1",
      invFinalVolumen: "",
      invFinalAlc: "",
      claseObtenida: "",
      edadObtenida: ""
    }];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Selección Inteligente de Lote Origen desde Graneles
  const handleLoteSelect = (e) => {
    const selectedKey = e.target.value;

    if (!selectedKey) {
      setFormData(prev => ({
        ...prev,
        noLote: "",
        loteOrigen: "",
        id_bitacora_granel: null,
        granelOrigenId: null,
        granelVolDisponible: 0,
        granelAlcGrad: "",
        categoria: "Mezcal Artesanal",
        clase: "Blanco Joven",
        claseOrigen: "Blanco Joven",
        tipoMaguey: "",
        especie: "",
        noAnalisis: "",
        no_analisis: "",
        clave_fq: ""
      }));
      return;
    }

    const rawId = selectedKey.replace("GRANEL:", "").replace("DESTILACION:", "").trim();
    const gRec = (granelRecords || []).find(g => {
      const gId = String(g.id !== undefined && g.id !== null ? g.id : "").trim();
      const gDbId = String(g.id_bitacora_granel !== undefined && g.id_bitacora_granel !== null ? g.id_bitacora_granel : (g.dbId || "")).trim();
      const gLote = String(g.noLote || g.no_lote || "").trim();
      const gCont = String(g.idContenedor || g.no_tanque || "").trim();

      return (
        (gId && gId === rawId) ||
        (gDbId && gDbId === rawId) ||
        (gLote && gLote === rawId) ||
        (gCont && gCont === rawId)
      );
    });

    if (gRec) {
      const entVol = parseFloat(gRec.entradaVolumen || gRec.invInicialVol || gRec.litros || 0);
      const salVol = parseFloat(gRec.salidaVolumen || 0);
      const volDisp = gRec.invFinalVol !== undefined && gRec.invFinalVol !== ""
        ? parseFloat(gRec.invFinalVol)
        : (gRec.litros !== undefined ? parseFloat(gRec.litros) : Math.max(0, entVol - salVol));
      const alc = gRec.porcentaje_alc_vol || gRec.invFinalAlc || gRec.entradaAlc || gRec.invInicialAlc || gRec.alcFinal || gRec.alcInicial || "";
      const maguey = gRec.tipoMaguey || gRec.especie || gRec.tipo_maguey || gRec.maguey || "";
      const fq = gRec.clave_fq || gRec.claveFQ || gRec.noAnalisis || gRec.noAnalisisFisicoquimico || gRec.no_analisis || gRec.analisisOrigen || "";
      const categoria = gRec.categoria || "Mezcal Artesanal";
      const clase = gRec.clase || gRec.clase_origen || gRec.claseOrigen || "Blanco Joven";
      const idUnidad = gRec.id_unidad_produccion || gRec.idUnidadProduccion || null;
      const instalacion = gRec.instalacion || "";
      const loteName = gRec.noLote || gRec.no_lote || gRec.idContenedor || rawId;

      setFormData(prev => {
        const defaultMadLote = (!prev.noLote || prev.noLote.startsWith("MAD-"))
          ? `MAD-${loteName}`
          : prev.noLote;

        const baseRecs = (prev.recipientes && prev.recipientes.length > 0)
          ? prev.recipientes
          : [{
              id: Date.now(),
              tipoRecipiente: "BARRICAS DE MADERA DE",
              detalleRecipiente: "",
              noRecipientes: "1",
              invInicialFecha: prev.fechaMovimiento || today,
              invInicialRecipientes: "1",
              invInicialVolumen: "",
              invInicialAlc: String(alc || ""),
              entradaFolio: "",
              entradaRecipientes: "0",
              entradaVolumen: "0",
              entradaAlc: "0",
              salidaFecha: "",
              salidaRecipientes: "0",
              salidaVolumen: "",
              salidaAlc: "",
              salidaMerma: "",
              nuevoLote: "",
              nuevo_lote: "",
              invFinalRecipientes: "1",
              invFinalVolumen: "",
              invFinalAlc: "",
              claseObtenida: "",
              edadObtenida: ""
            }];

        const updatedRecs = baseRecs.map(r => ({
          ...r,
          invInicialAlc: (!r.invInicialAlc || r.invInicialAlc === prev.granelAlcGrad) ? (alc ? String(alc) : r.invInicialAlc) : r.invInicialAlc,
          invInicialFecha: r.invInicialFecha || prev.fechaMovimiento || today
        }));

        return {
          ...prev,
          noLote: defaultMadLote,
          loteOrigen: loteName,
          id_bitacora_granel: gRec.id_bitacora_granel || (typeof gRec.id === "number" ? gRec.id : null),
          granelOrigenId: gRec.id || gRec.id_bitacora_granel || rawId,
          granelVolDisponible: volDisp,
          granelAlcGrad: String(alc || ""),
          tipoMaguey: maguey,
          especie: maguey,
          categoria: categoria,
          clase: clase,
          claseOrigen: clase,
          noAnalisis: fq,
          no_analisis: fq,
          clave_fq: fq,
          id_unidad_produccion: idUnidad || prev.id_unidad_produccion,
          instalacion: instalacion || prev.instalacion,
          recipientes: updatedRecs
        };
      });
    } else {
      setFormData(prev => ({
        ...prev,
        noLote: (!prev.noLote || prev.noLote.startsWith("MAD-")) ? `MAD-${rawId}` : prev.noLote,
        loteOrigen: rawId,
        granelOrigenId: null,
        granelVolDisponible: 0
      }));
    }
  };

  const handleAddRecipiente = () => {
    const newRec = {
      id: Date.now(),
      tipoRecipiente: "BARRICAS DE MADERA DE",
      detalleRecipiente: "",
      noRecipientes: "1",
      invInicialFecha: formData?.fechaMovimiento || today,
      invInicialRecipientes: "1",
      invInicialVolumen: "",
      invInicialAlc: formData?.granelAlcGrad || "",
      entradaFolio: "",
      entradaRecipientes: "0",
      entradaVolumen: "0",
      entradaAlc: "0",
      salidaFecha: "",
      salidaRecipientes: "0",
      salidaVolumen: "",
      salidaAlc: "",
      salidaMerma: "",
      nuevoLote: "",
      nuevo_lote: "",
      invFinalRecipientes: "1",
      invFinalVolumen: "",
      invFinalAlc: "",
      claseObtenida: "",
      edadObtenida: ""
    };
    setFormData(prev => ({
      ...prev,
      recipientes: [...recipientes, newRec]
    }));
  };

  const handleRemoveRecipiente = (id) => {
    if (recipientes.length === 1) {
      alert("Debe incluir al menos un recipiente en el proceso de maduración.");
      return;
    }
    setFormData(prev => ({
      ...prev,
      recipientes: recipientes.filter(r => r.id !== id)
    }));
  };

  const handleRecipienteChange = (id, field, value) => {
    setFormData(prev => {
      const updatedRecs = (prev.recipientes || recipientes).map(rec => {
        if (rec.id !== id) return rec;

        const updated = { ...rec, [field]: value };

        const fechaInicio = updated.invInicialFecha || prev.fechaMovimiento || today;
        const fechaFin = updated.salidaFecha;

        // Evaluar NOM-070 solo cuando existan ambas fechas
        const normaResult = evaluarNormaClase(updated.tipoRecipiente, fechaInicio, fechaFin);
        updated.claseObtenida = normaResult.evaluada ? normaResult.clase : "";
        updated.edadObtenida = normaResult.evaluada ? normaResult.edad : "";

        // Si se evalúa NOM-070 y no se ha especificado un nuevo lote obtenido, sugerir uno representativo
        if (normaResult.evaluada && !updated.nuevoLote && (updated.salidaVolumen || updated.salidaFecha)) {
          let prefix = "MAD-";
          const clLower = (normaResult.clase || "").toLowerCase();
          if (clLower.includes("añejo") || clLower.includes("anejo")) prefix = "AÑE-";
          else if (clLower.includes("reposado")) prefix = "REP-";
          else if (clLower.includes("vidrio")) prefix = "MAD-VID-";

          const baseName = prev.noLote || prev.loteOrigen || `LOTE-${Date.now().toString().slice(-4)}`;
          const cleanBase = baseName.replace(/^(MAD-|AÑE-|REP-|MAD-VID-)/, '');
          updated.nuevoLote = `${prefix}${cleanBase}`;
          updated.nuevo_lote = updated.nuevoLote;
        }

        if (field === "nuevoLote" || field === "nuevo_lote") {
          updated.nuevoLote = value;
          updated.nuevo_lote = value;
        }

        const vIni = parseFloat(updated.invInicialVolumen || 0);
        const vEnt = parseFloat(updated.entradaVolumen || 0);
        const vSal = parseFloat(updated.salidaVolumen || 0);
        const vMer = parseFloat(updated.salidaMerma || 0);

        // Saldo final: si hay salida registrada se calcula (vIni + vEnt) - (vSal + vMer),
        // si no hay salida registrada aún, el saldo final es el total ingresado.
        if (field === "invInicialVolumen" || field === "entradaVolumen" || field === "salidaVolumen" || field === "salidaMerma") {
          const saldoFinal = Math.max(0, (vIni + vEnt) - (vSal + vMer));
          updated.invFinalVolumen = saldoFinal.toString();
        }

        return updated;
      });

      return { ...prev, recipientes: updatedRecs };
    });
  };

  const handleNewRecord = () => {
    // Si solo hay un lote a granel registrado, pre-cargarlo inmediatamente con todas sus opciones
    const autoLot = (granelRecords && granelRecords.length === 1) ? granelRecords[0] : null;
    let autoEntVol = 0;
    let autoSalVol = 0;
    let autoVolDisp = 0;
    let autoAlc = "";
    let autoMaguey = "";
    let autoFq = "";
    let autoCat = "Mezcal Artesanal";
    let autoClase = "Blanco Joven";
    let autoIdGranel = null;
    let autoLoteName = "";
    let autoIdUnidad = null;
    let autoInstalacion = "";

    if (autoLot) {
      autoEntVol = parseFloat(autoLot.entradaVolumen || autoLot.invInicialVol || autoLot.litros || 0);
      autoSalVol = parseFloat(autoLot.salidaVolumen || 0);
      autoVolDisp = autoLot.invFinalVol !== undefined && autoLot.invFinalVol !== ""
        ? parseFloat(autoLot.invFinalVol)
        : (autoLot.litros !== undefined ? parseFloat(autoLot.litros) : Math.max(0, autoEntVol - autoSalVol));
      autoAlc = autoLot.porcentaje_alc_vol || autoLot.invFinalAlc || autoLot.entradaAlc || autoLot.invInicialAlc || autoLot.alcFinal || autoLot.alcInicial || "";
      autoMaguey = autoLot.tipoMaguey || autoLot.especie || autoLot.tipo_maguey || autoLot.maguey || "";
      autoFq = autoLot.clave_fq || autoLot.claveFQ || autoLot.noAnalisis || autoLot.noAnalisisFisicoquimico || autoLot.no_analisis || autoLot.analisisOrigen || "";
      autoCat = autoLot.categoria || "Mezcal Artesanal";
      autoClase = autoLot.clase || autoLot.clase_origen || autoLot.claseOrigen || "Blanco Joven";
      autoIdGranel = autoLot.id_bitacora_granel || (typeof autoLot.id === "number" ? autoLot.id : null);
      autoLoteName = autoLot.noLote || autoLot.no_lote || autoLot.idContenedor || "";
      autoIdUnidad = autoLot.id_unidad_produccion || autoLot.idUnidadProduccion || null;
      autoInstalacion = autoLot.instalacion || "";
    }

    setFormData({
      id: `MAD-${Date.now()}`,
      fechaMovimiento: today,
      noLote: autoLoteName ? `MAD-${autoLoteName}` : "",
      loteOrigen: autoLoteName,
      id_bitacora_granel: autoIdGranel,
      granelOrigenId: autoLot ? (autoLot.id || autoLot.id_bitacora_granel) : null,
      granelVolDisponible: autoVolDisp,
      granelAlcGrad: String(autoAlc || ""),
      categoria: autoCat,
      clase: autoClase,
      claseOrigen: autoClase,
      tipoMaguey: autoMaguey,
      especie: autoMaguey,
      noAnalisis: autoFq,
      no_analisis: autoFq,
      clave_fq: autoFq,
      id_unidad_produccion: autoIdUnidad,
      instalacion: autoInstalacion,
      recipientes: [{
        id: Date.now(),
        tipoRecipiente: "BARRICAS DE MADERA DE",
        detalleRecipiente: "",
        noRecipientes: "1",
        invInicialFecha: today,
        invInicialRecipientes: "1",
        invInicialVolumen: "",
        invInicialAlc: String(autoAlc || ""),
        entradaFolio: "",
        entradaRecipientes: "0",
        entradaVolumen: "0",
        entradaAlc: "0",
        salidaFecha: "",
        salidaRecipientes: "0",
        salidaVolumen: "",
        salidaAlc: "",
        salidaMerma: "",
        nuevoLote: "",
        nuevo_lote: "",
        invFinalRecipientes: "1",
        invFinalVolumen: "",
        invFinalAlc: "",
        claseObtenida: "",
        edadObtenida: ""
      }],
      observaciones: "",
      status: "borrador"
    });
    setIsFormOpen(true);
  };

  const handleEditRecord = (rec) => {
    if (rec.status !== "borrador" && rec.status !== "rechazado" && rec.estatus !== "borrador" && rec.estatus !== "rechazado") {
      alert("Solo se pueden editar registros que se encuentren en estado BORRADOR o RECHAZADO.");
      return;
    }
    const primerRec = (rec.recipientes && rec.recipientes[0]) || {};
    const fq = rec.noAnalisis || rec.no_analisis || rec.clave_fq || rec.claveFQ || "";
    const maguey = rec.tipoMaguey || rec.tipo_maguey || rec.especie || "";
    const alc = rec.granelAlcGrad || rec.invInicialAlc || primerRec.invInicialAlc || primerRec.inv_inicial_alc || "";

    setFormData({
      id: rec.id || rec.id_bitacora_maduracion,
      id_bitacora_maduracion: rec.id_bitacora_maduracion || rec.id,
      dbId: rec.id_bitacora_maduracion || (typeof rec.id === "number" ? rec.id : null),
      fechaMovimiento: rec.fechaMovimiento || rec.fecha_inicio_maduracion || today,
      noLote: rec.noLote || rec.lote_maduracion || "",
      loteOrigen: rec.loteOrigen || rec.no_lote_origen || "",
      id_bitacora_granel: rec.id_bitacora_granel || rec.granelOrigenId || null,
      granelOrigenId: rec.id_bitacora_granel || rec.granelOrigenId || null,
      categoria: rec.categoria || "Mezcal Artesanal",
      clase: rec.clase || rec.clase_obtenida || rec.clase_origen || "Blanco Joven",
      claseOrigen: rec.clase_origen || rec.claseOrigen || "Blanco Joven",
      tipoMaguey: maguey,
      especie: maguey,
      noAnalisis: fq,
      no_analisis: fq,
      clave_fq: fq,
      nuevo_lote: rec.nuevo_lote || rec.nuevoLote || primerRec.nuevo_lote || primerRec.nuevoLote || "",
      nuevoLote: rec.nuevo_lote || rec.nuevoLote || primerRec.nuevo_lote || primerRec.nuevoLote || "",
      granelAlcGrad: String(alc || ""),
      granelVolDisponible: rec.granelVolDisponible || rec.volumen_inicial_total || 0,
      id_unidad_produccion: rec.id_unidad_produccion || null,
      instalacion: rec.instalacion || "",
      recipientes: rec.recipientes && rec.recipientes.length > 0 ? rec.recipientes : [{
        id: Date.now(),
        tipoRecipiente: rec.tipoRecipiente || rec.tipo_recipiente || "BARRICAS DE MADERA DE",
        detalleRecipiente: rec.detalleRecipiente || rec.detalle_recipiente || "",
        noRecipientes: rec.invInicialRecipientes || "1",
        invInicialFecha: rec.invInicialFecha || rec.fechaMovimiento || today,
        invInicialRecipientes: rec.invInicialRecipientes || "1",
        invInicialVolumen: rec.invInicialVolumen || rec.volumen_inicial_total || "",
        invInicialAlc: rec.invInicialAlc || primerRec.inv_inicial_alc || String(alc || ""),
        entradaFolio: rec.entradaFolio || "",
        entradaRecipientes: rec.entradaRecipientes || "0",
        entradaVolumen: rec.entradaVolumen || "0",
        entradaAlc: rec.entradaAlc || "0",
        salidaFecha: rec.salidaFecha || "",
        salidaRecipientes: rec.salidaRecipientes || "0",
        salidaVolumen: rec.salidaVolumen || "",
        salidaAlc: rec.salidaAlc !== undefined && rec.salidaAlc !== null && rec.salidaAlc !== "" ? rec.salidaAlc : (rec.salida_alc || ""),
        salidaMerma: rec.salidaMerma || "",
        nuevoLote: rec.nuevo_lote || rec.nuevoLote || primerRec.nuevo_lote || primerRec.nuevoLote || "",
        nuevo_lote: rec.nuevo_lote || rec.nuevoLote || primerRec.nuevo_lote || primerRec.nuevoLote || "",
        invFinalRecipientes: rec.invFinalRecipientes || "1",
        invFinalVolumen: rec.invFinalVolumen || rec.saldo_final_total || "",
        invFinalAlc: rec.invFinalAlc || "",
        claseObtenida: rec.claseObtenida || rec.clase_obtenida || "",
        edadObtenida: rec.edadObtenida || rec.edad_obtenida || ""
      }],
      observaciones: rec.observaciones || "",
      status: rec.status || rec.estatus || "borrador"
    });
    setIsFormOpen(true);
  };

  const handleSubmitForm = (statusToSet = "revision") => {
    const primerRec = recipientes[0] || {};
    const totalIni = recipientes.reduce((sum, r) => sum + parseFloat(r.invInicialVolumen || 0), 0);
    const totalEnt = recipientes.reduce((sum, r) => sum + parseFloat(r.entradaVolumen || 0), 0);
    const totalSal = recipientes.reduce((sum, r) => sum + parseFloat(r.salidaVolumen || 0), 0);
    const totalMer = recipientes.reduce((sum, r) => sum + parseFloat(r.salidaMerma || 0), 0);
    const totalSaldo = recipientes.reduce((sum, r) => {
      const vFin = r.invFinalVolumen !== undefined && r.invFinalVolumen !== ""
        ? parseFloat(r.invFinalVolumen)
        : Math.max(0, (parseFloat(r.invInicialVolumen || 0) + parseFloat(r.entradaVolumen || 0)) - (parseFloat(r.salidaVolumen || 0) + parseFloat(r.salidaMerma || 0)));
      return sum + vFin;
    }, 0);

    const recordToSave = {
      ...formData,
      id: formData.id || `MAD-${Date.now()}`,
      dbId: formData.dbId || (typeof formData.id === "number" ? formData.id : null),
      fecha_inicio_maduracion: formData.fechaMovimiento || today,
      fechaMovimiento: formData.fechaMovimiento || today,
      no_lote_origen: formData.loteOrigen || formData.noLote || "LOTE-TEMP",
      loteOrigen: formData.loteOrigen || formData.noLote || "LOTE-TEMP",
      lote_maduracion: formData.noLote || formData.lote_maduracion || `MAD-${Date.now()}`,
      noLote: formData.noLote || formData.lote_maduracion || `MAD-${Date.now()}`,
      categoria: formData.categoria || "Mezcal Artesanal",
      clase_origen: formData.claseOrigen || formData.clase || "Blanco Joven",
      tipo_maguey: formData.tipoMaguey || "",
      tipoMaguey: formData.tipoMaguey || "",
      no_analisis: formData.noAnalisis || "",
      noAnalisis: formData.noAnalisis || "",
      volumen_inicial_total: totalIni,
      volumen_entrada_total: totalEnt,
      volumen_salida_total: totalSal,
      salida_alc: primerRec.salidaAlc || primerRec.salida_alc || null,
      salidaAlc: primerRec.salidaAlc || primerRec.salida_alc || null,
      merma_total: totalMer,
      saldo_final_total: totalSaldo,
      clase_obtenida: primerRec.claseObtenida || null,
      edad_obtenida: primerRec.edadObtenida || null,
      clase: primerRec.claseObtenida || formData.clase || "Blanco Joven",
      nuevo_lote: primerRec.nuevoLote || primerRec.nuevo_lote || formData.nuevo_lote || formData.nuevoLote || "",
      nuevoLote: primerRec.nuevoLote || primerRec.nuevo_lote || formData.nuevo_lote || formData.nuevoLote || "",
      recipientes: recipientes.map(r => ({
        ...r,
        salida_alc: r.salidaAlc !== "" && r.salidaAlc !== undefined ? r.salidaAlc : (r.salida_alc || 0),
        salidaAlc: r.salidaAlc !== "" && r.salidaAlc !== undefined ? r.salidaAlc : (r.salida_alc || 0),
        nuevo_lote: r.nuevoLote || r.nuevo_lote || primerRec.nuevoLote || primerRec.nuevo_lote || "",
        nuevoLote: r.nuevoLote || r.nuevo_lote || primerRec.nuevoLote || primerRec.nuevo_lote || ""
      })),
      observaciones: formData.observaciones || "",
      estatus: statusToSet === "revision" ? "enviada" : "borrador",
      status: statusToSet
    };

    handleSaveMaduracion(recordToSave);
  };

  // =========================================================================
  // HELPER MEMOIZADO: Lotes disponibles para Salida (Maduración y Granel)
  // =========================================================================
  const availableMaduracionSalidaLots = React.useMemo(() => {
    const list = [];

    // 1. Lotes registrados en Maduración (Sección VII) con saldo disponible
    (maduracionRecords || []).forEach((m) => {
      const mId = String(m.id_bitacora_maduracion || m.id || m.dbId || "");
      const primerRec = (m.recipientes && m.recipientes[0]) || {};
      const vIni = parseFloat(m.volumen_inicial_total || m.invInicialVolumen || primerRec.inv_inicial_volumen || primerRec.invInicialVolumen || 0);
      const vSal = parseFloat(m.volumen_salida_total || m.salidaVolumen || primerRec.salida_volumen || primerRec.salidaVolumen || 0);
      const vMer = parseFloat(m.merma_total || m.salidaMerma || primerRec.salida_merma || primerRec.salidaMerma || 0);
      const saldoDisp = m.saldo_final_total !== undefined && m.saldo_final_total !== null && m.saldo_final_total !== ""
        ? parseFloat(m.saldo_final_total)
        : Math.max(0, vIni - (vSal + vMer));

      if (saldoDisp > 0 || (vIni > 0 && vSal === 0)) {
        const loteMad = m.noLote || m.lote_maduracion || `MAD-${mId}`;
        const loteOrigen = m.loteOrigen || m.no_lote_origen || "-";
        const tipoRec = m.tipoRecipiente || m.tipo_recipiente || primerRec.tipo_recipiente || primerRec.tipoRecipiente || "BARRICAS DE MADERA DE ROBLE";
        const key = `MAD:${mId}`;

        // Intentar obtener maguey de granelRecords si no viene en m
        let magueySrc = m.tipoMaguey || m.tipo_maguey || "";
        let catSrc = m.categoria || "Mezcal Artesanal";
        let claseOrigSrc = m.clase_origen || m.claseOrigen || m.clase || "Blanco Joven";

        const matchG = (granelRecords || []).find(g =>
          String(g.id || g.id_bitacora_granel) === String(m.id_bitacora_granel) ||
          g.noLote === loteOrigen || g.no_lote === loteOrigen
        );
        if (matchG) {
          if (!magueySrc) magueySrc = matchG.tipoMaguey || matchG.especie || matchG.tipo_maguey || "";
          if (!catSrc || catSrc === "Mezcal Artesanal") catSrc = matchG.categoria || catSrc;
          if (!claseOrigSrc) claseOrigSrc = matchG.clase || claseOrigSrc;
        }

        list.push({
          key,
          idKey: key,
          rawId: mId,
          type: "maduracion",
          sourceRecord: m,
          originGranelId: m.id_bitacora_granel || (matchG ? (matchG.id_bitacora_granel || matchG.id) : null),
          loteMad,
          loteOrigen,
          saldoDisponible: saldoDisp > 0 ? saldoDisp : vIni,
          tipoRecipiente: tipoRec,
          maguey: magueySrc,
          categoria: catSrc,
          claseOrigen: claseOrigSrc,
          fechaInicio: m.fechaMovimiento || m.fecha_inicio_maduracion || primerRec.inv_inicial_fecha || primerRec.invInicialFecha || today,
          alc: primerRec.salida_alc || primerRec.salidaAlc || primerRec.inv_inicial_alc || m.salida_alc || m.salidaAlc || 45,
          recipientesCount: primerRec.salida_recipientes || primerRec.salidaRecipientes || primerRec.inv_inicial_recipientes || m.invInicialRecipientes || 1,
          label: `🏺 [Maduración] Lote: ${loteMad} | Origen: ${loteOrigen} | Saldo: ${saldoDisp.toLocaleString()} L | ${tipoRec}`
        });
      }
    });

    // 2. Lotes registrados en Granel (Sección V) que sean de maduración o con saldo disponible
    (granelRecords || []).forEach((g, gIdx) => {
      const gId = String(g.id_bitacora_granel || g.id || g.dbId || gIdx);
      const entVol = parseFloat(g.entradaVolumen || g.invInicialVol || g.litros || 0);
      const salVol = parseFloat(g.salidaVolumen || 0);
      const saldoDisp = g.invFinalVol !== undefined && g.invFinalVol !== "" && !isNaN(Number(g.invFinalVol))
        ? parseFloat(g.invFinalVol)
        : (g.litros !== undefined && g.litros !== "" && !isNaN(Number(g.litros))
            ? parseFloat(g.litros)
            : Math.max(0, entVol - salVol));

      if (saldoDisp <= 0) return;

      const clase = String(g.clase || "").toLowerCase();
      const tipoGranel = String(g.tipo_granel || "").toLowerCase();
      const barricas = String(g.no_barricas || "").trim();
      const destino = String(g.salidaDestino || "").toLowerCase();

      const esMaduracion = 
        /reposad|añej|vidrio|madur/.test(clase) ||
        (barricas !== "" && barricas !== "0" && !barricas.startsWith("0 ")) ||
        /madur/.test(tipoGranel) ||
        /madur/.test(destino) ||
        Boolean(g.id_bitacora_maduracion);

      const loteName = g.noLote || g.no_lote || g.idContenedor || `Granel #${gId}`;
      const isVidrio = (barricas && barricas.toLowerCase().includes("vidrio")) || clase.includes("vidrio");
      const tipoRec = isVidrio
        ? "RECIPIENTES DE VIDRIO (GARRAFONES / DAMAJUANAS)"
        : (barricas || "BARRICAS DE MADERA DE ROBLE");
      const fechaIni = g.fecha || (g.createdAt ? String(g.createdAt).split("T")[0] : today);
      const alcVal = parseFloat(g.porcentaje_alc_vol || g.invFinalAlc || g.entradaAlc || g.invInicialAlc || 45) || 45;
      const magueyVal = g.tipoMaguey || g.especie || g.tipo_maguey || g.maguey || "";
      const catVal = g.categoria || "Mezcal Artesanal";
      const key = `GRANEL:${gId}`;

      list.push({
        key,
        idKey: key,
        rawId: gId,
        type: esMaduracion ? "granel_maduracion" : "granel_general",
        sourceRecord: g,
        originGranelId: g.id_bitacora_granel || g.id || g.dbId,
        loteMad: `MAD-${loteName}`,
        loteOrigen: loteName,
        saldoDisponible: saldoDisp,
        tipoRecipiente: tipoRec,
        maguey: magueyVal,
        categoria: catVal,
        claseOrigen: g.clase || "Blanco Joven",
        fechaInicio: fechaIni,
        alc: alcVal,
        recipientesCount: 1,
        label: esMaduracion
          ? `📦 [Granel Maduración] Lote: ${loteName} (${g.clase || 'Maduración'}${barricas ? ' - ' + barricas : ''}) | Saldo: ${saldoDisp.toLocaleString()} L`
          : `📦 [Granel] Lote: ${loteName} (${g.clase || 'Blanco Joven'}) | Saldo: ${saldoDisp.toLocaleString()} L`
      });
    });

    return list;
  }, [maduracionRecords, granelRecords, today]);

  // Lote actualmente seleccionado en el Modal de Salida
  const currentSelectedLot = React.useMemo(() => {
    if (!salidaForm.maduracionId) return null;
    const selKey = String(salidaForm.maduracionId);
    return availableMaduracionSalidaLots.find(
      (opt) => opt.key === selKey ||
               opt.rawId === selKey ||
               opt.loteMad === selKey ||
               opt.loteOrigen === selKey ||
               opt.idKey === selKey
    ) || null;
  }, [salidaForm.maduracionId, availableMaduracionSalidaLots]);

  const currentSelectedMaduracion = currentSelectedLot ? currentSelectedLot.sourceRecord : null;
  const currentTipoRecipiente = currentSelectedLot?.tipoRecipiente || "BARRICAS DE MADERA DE ROBLE";
  const currentFechaInicio = currentSelectedLot?.fechaInicio || today;
  const currentMaguey = currentSelectedLot?.maguey || "";
  const currentCategoria = currentSelectedLot?.categoria || "Mezcal Artesanal";
  const currentClaseOrigen = currentSelectedLot?.claseOrigen || "Blanco Joven";
  const currentLoteMaduracion = currentSelectedLot?.loteMad || "";
  const currentLoteOrigen = currentSelectedLot?.loteOrigen || "-";
  const currentSaldoDisponible = currentSelectedLot?.saldoDisponible || 0;
  const currentVolInicial = currentSelectedLot?.saldoDisponible || 0;

  const currentVolSalida = parseFloat(salidaForm.volumenSalida || 0);
  const currentMerma = parseFloat(salidaForm.merma || 0);
  const currentSaldoRemanente = Math.max(0, currentSaldoDisponible - (currentVolSalida + currentMerma));

  const currentDictamen = evaluarNormaClase(currentTipoRecipiente, currentFechaInicio, salidaForm.fechaSalida);

  const handleOpenSalidaModal = (recordParam = null) => {
    if (!availableMaduracionSalidaLots || availableMaduracionSalidaLots.length === 0) {
      Swal.fire({
        title: "Sin Lotes Disponibles",
        text: "No se encontraron lotes de maduración o graneles con saldo disponible para dar salida.",
        icon: "info",
        confirmButtonColor: "#D97706"
      });
      return;
    }

    let targetOpt = null;
    if (recordParam) {
      const pId = String(recordParam.id_bitacora_maduracion || recordParam.id || recordParam.dbId || "");
      const pLote = String(recordParam.noLote || recordParam.lote_maduracion || recordParam.no_lote || "");
      targetOpt = availableMaduracionSalidaLots.find(
        (opt) => opt.rawId === pId || opt.loteMad === pLote || opt.loteOrigen === pLote || opt.key === `MAD:${pId}`
      );
    }
    if (!targetOpt) {
      targetOpt = availableMaduracionSalidaLots[0];
    }

    const evaluacion = evaluarNormaClase(targetOpt.tipoRecipiente, targetOpt.fechaInicio, today);
    const prefijo = evaluacion.clase === "Añejo" ? "AÑE" : (evaluacion.clase === "Reposado" ? "REP" : (evaluacion.clase.toLowerCase().includes("vidrio") ? "MAD-VID" : "MAD"));
    const baseLote = targetOpt.loteMad.replace(/^MAD-/, "");
    const sugLote = `${prefijo}-${baseLote}`;

    setSalidaForm({
      maduracionId: targetOpt.key,
      fechaSalida: today,
      volumenSalida: targetOpt.saldoDisponible > 0 ? String(targetOpt.saldoDisponible) : "",
      merma: "0",
      salidaAlc: String(targetOpt.alc || 45),
      salidaRecipientes: String(targetOpt.recipientesCount || 1),
      nuevoLote: sugLote,
      destino: "Tanque de Mezcal a Granel",
      observaciones: ""
    });

    setIsSalidaModalOpen(true);
  };

  const handleSelectLoteSalida = (selectedKey) => {
    const targetOpt = availableMaduracionSalidaLots.find(
      (opt) => opt.key === selectedKey || opt.rawId === selectedKey || opt.idKey === selectedKey
    );
    if (!targetOpt) return;

    const fechaSal = salidaForm.fechaSalida || today;
    const evaluacion = evaluarNormaClase(targetOpt.tipoRecipiente, targetOpt.fechaInicio, fechaSal);
    const prefijo = evaluacion.clase === "Añejo" ? "AÑE" : (evaluacion.clase === "Reposado" ? "REP" : (evaluacion.clase.toLowerCase().includes("vidrio") ? "MAD-VID" : "MAD"));
    const baseLote = targetOpt.loteMad.replace(/^MAD-/, "");
    const sugLote = `${prefijo}-${baseLote}`;

    setSalidaForm((prev) => ({
      ...prev,
      maduracionId: targetOpt.key,
      volumenSalida: targetOpt.saldoDisponible > 0 ? String(targetOpt.saldoDisponible) : "",
      merma: "0",
      salidaAlc: String(targetOpt.alc || 45),
      salidaRecipientes: String(targetOpt.recipientesCount || 1),
      nuevoLote: sugLote
    }));
  };

  const handleFechaSalidaChange = (newFecha) => {
    setSalidaForm((prev) => {
      const nextState = { ...prev, fechaSalida: newFecha };
      if (currentSelectedLot) {
        const evaluacion = evaluarNormaClase(currentTipoRecipiente, currentFechaInicio, newFecha);
        const prefijo = evaluacion.clase === "Añejo" ? "AÑE" : (evaluacion.clase === "Reposado" ? "REP" : (evaluacion.clase.toLowerCase().includes("vidrio") ? "MAD-VID" : "MAD"));
        const baseLote = currentLoteMaduracion.replace(/^MAD-/, "");
        nextState.nuevoLote = `${prefijo}-${baseLote}`;
      }
      return nextState;
    });
  };

  const handleSaveSalidaMaduracion = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!currentSelectedLot) {
      Swal.fire("Atención", "Debe seleccionar un lote en maduración.", "warning");
      return;
    }

    const volSal = parseFloat(salidaForm.volumenSalida || 0);
    const merma = parseFloat(salidaForm.merma || 0);
    const alc = parseFloat(salidaForm.salidaAlc || 0);
    const maxPermitido = currentSaldoDisponible;

    if (volSal <= 0) {
      Swal.fire("Volumen inválido", "El volumen a dar de salida debe ser mayor a 0 litros.", "warning");
      return;
    }

    if (volSal + merma > maxPermitido + 0.001) {
      Swal.fire(
        "Volumen excedido",
        `La suma de volumen de salida (${volSal} L) y merma (${merma} L) no puede superar el volumen disponible (${maxPermitido} L).`,
        "error"
      );
      return;
    }

    if (salidaForm.fechaSalida < currentFechaInicio) {
      Swal.fire("Fecha inválida", "La fecha de salida no puede ser anterior a la fecha de ingreso a maduración.", "warning");
      return;
    }

    if (alc <= 0) {
      Swal.fire("Grado inválido", "Debe especificar un grado alcohólico válido (% Alc. Vol.).", "warning");
      return;
    }

    const isMaduracionOrigin = currentSelectedLot.type === "maduracion";
    const src = currentSelectedLot.sourceRecord;

    let targetId = null;
    let idGranelOrigen = currentSelectedLot.originGranelId || null;
    let loteOrigenStr = currentLoteOrigen;

    if (isMaduracionOrigin) {
      targetId = src.dbId || src.id_bitacora_maduracion || (typeof src.id === "number" ? src.id : null);
      if (!idGranelOrigen) idGranelOrigen = src.id_bitacora_granel || src.granelOrigenId || null;
      loteOrigenStr = src.loteOrigen || src.no_lote_origen || currentLoteOrigen;
    } else {
      idGranelOrigen = src.id_bitacora_granel || src.id || src.dbId || null;
      loteOrigenStr = src.noLote || src.no_lote || src.idContenedor || currentLoteOrigen;
    }

    const updatedRecipientes = [{
      num_recipiente: "1",
      tipo_recipiente: currentTipoRecipiente,
      detalle_recipiente: src.detalleRecipiente || src.detalle_recipiente || (src.recipientes && src.recipientes[0]?.detalle_recipiente) || "",
      capacidad_recipiente: currentSaldoDisponible,
      inv_inicial_fecha: currentFechaInicio,
      inv_inicial_recipientes: parseInt(salidaForm.salidaRecipientes || 1, 10),
      inv_inicial_volumen: currentSaldoDisponible,
      inv_inicial_alc: currentSelectedLot.alc || alc,
      salida_fecha: salidaForm.fechaSalida,
      salida_recipientes: parseInt(salidaForm.salidaRecipientes || 1, 10),
      salida_volumen: volSal,
      salida_alc: alc,
      salida_merma: merma,
      inv_final_recipientes: parseInt(salidaForm.salidaRecipientes || 1, 10),
      inv_final_volumen: currentSaldoRemanente,
      inv_final_alc: alc,
      clase_obtenida: currentDictamen.clase,
      edad_obtenida: currentDictamen.edad,
      nuevo_lote: salidaForm.nuevoLote,
      nuevoLote: salidaForm.nuevoLote
    }];

    const recordToSave = {
      ...src,
      id: targetId,
      dbId: targetId,
      id_bitacora_maduracion: targetId,
      id_bitacora_granel: idGranelOrigen,
      granelOrigenId: idGranelOrigen,
      no_lote_origen: loteOrigenStr,
      loteOrigen: loteOrigenStr,
      lote_maduracion: currentLoteMaduracion,
      noLote: currentLoteMaduracion,
      tipo_maguey: currentMaguey,
      tipoMaguey: currentMaguey,
      categoria: currentCategoria,
      clase_origen: currentClaseOrigen,
      claseOrigen: currentClaseOrigen,
      fecha_inicio_maduracion: currentFechaInicio,
      fechaMovimiento: currentFechaInicio,
      fecha_salida_maduracion: salidaForm.fechaSalida,
      salidaFecha: salidaForm.fechaSalida,
      volumen_inicial_total: currentSaldoDisponible,
      volumen_salida_total: volSal,
      salidaVolumen: volSal,
      salida_alc: alc,
      salidaAlc: alc,
      merma_total: merma,
      salidaMerma: merma,
      saldo_final_total: currentSaldoRemanente,
      clase_obtenida: currentDictamen.clase,
      claseObtenida: currentDictamen.clase,
      edad_obtenida: currentDictamen.edad,
      edadObtenida: currentDictamen.edad,
      clase: currentDictamen.clase,
      nuevo_lote: salidaForm.nuevoLote,
      nuevoLote: salidaForm.nuevoLote,
      no_tanque: salidaForm.destino || "Bodega Maduración",
      recipientes: updatedRecipientes,
      observaciones: (src.observaciones ? src.observaciones + " | " : "") +
        (salidaForm.observaciones ? salidaForm.observaciones : `Salida de maduración (${currentDictamen.clase}) de ${volSal} L el ${salidaForm.fechaSalida}. En proceso de validación UI.`),
      estatus: "enviada",
      status: "revision"
    };

    await handleSaveMaduracion(recordToSave);
    setIsSalidaModalOpen(false);

    Swal.fire({
      title: "Salida Enviada para Validación UI",
      html: `
        <div style="text-align: left; font-size: 13px; line-height: 1.6;">
          <p>Se ha registrado la salida de maduración del lote <strong>${currentLoteMaduracion}</strong>:</p>
          <ul>
            <li><strong>Volumen a extraer:</strong> ${volSal.toLocaleString()} L</li>
            <li><strong>Merma:</strong> ${merma.toLocaleString()} L</li>
            <li><strong>Clase dictaminada (NOM-070):</strong> <span style="color: #059669; font-weight: 800;">${currentDictamen.clase}</span></li>
            <li><strong>Tiempo de maduración:</strong> ${currentDictamen.edad}</li>
            <li><strong>Nuevo Lote a Granel:</strong> <span style="color: #7C3AED; font-weight: 800;">${salidaForm.nuevoLote}</span></li>
            <li><strong>Maguey origen:</strong> ${currentMaguey || "Maguey Espadín"}</li>
          </ul>
          <p style="margin-top: 10px; color: #D97706; font-size: 12px; background: rgba(217, 119, 6, 0.08); padding: 8px 12px; border-radius: 8px;">
            ℹ️ El registro fue enviado a la <strong>Unidad de Inspección</strong> para validación oficial. Al ser aprobado por el inspector, se creará automáticamente el nuevo lote en Granel (${salidaForm.nuevoLote}) y se descontará del saldo del lote origen (${loteOrigenStr}).
          </p>
        </div>
      `,
      icon: "success",
      confirmButtonColor: "#D97706"
    });
  };

  const totalVolumenMadurar = recipientes.reduce((sum, r) => sum + (parseFloat(r.invInicialVolumen || 0) + parseFloat(r.entradaVolumen || 0)), 0);
  const superaSaldoGranel = formData.granelVolDisponible > 0 && totalVolumenMadurar > formData.granelVolDisponible;

  return (
    <div className="animate-fadeIn">
      {/* HEADER DE LA SECCIÓN */}
      <div className="history-section-header" style={{ marginBottom: "20px" }}>
        <div>
          <h2 style={{ fontSize: "20px", fontWeight: 800, color: "var(--agave-green)" }}>
            Historial de Registro de Maduración (Sección VII)
          </h2>
          <p style={{ margin: "4px 0 0 0", color: "var(--text-muted)", fontSize: "13px" }}>
            Monitoreo en barricas de madera y recipientes de vidrio conforme a NOM-070-SCFI-2016
          </p>
        </div>

        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button
            type="button"
            onClick={() => handleOpenSalidaModal()}
            className="btn-add-new"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 18px",
              background: "linear-gradient(135deg, #D97706 0%, #B45309 100%)",
              boxShadow: "0 4px 14px rgba(217, 119, 6, 0.35)",
              border: "none",
              color: "#FFF",
              cursor: "pointer",
              borderRadius: "8px"
            }}
          >
            <HourglassEmptyIcon style={{ fontSize: 20 }} />
            <span style={{ fontWeight: 700 }}>Salida de Maduración</span>
          </button>

          <button
            type="button"
            onClick={handleNewRecord}
            className="btn-add-new"
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 18px" }}
          >
            <AddIcon style={{ fontSize: 20 }} />
            <span style={{ fontWeight: 700 }}>Nuevo Registro de Maduración</span>
          </button>
        </div>
      </div>

      {/* TABLA DE HISTORIAL DE MADURACIÓN */}
      <div
        className="records-list-wrapper"
        style={{
          borderRadius: "14px",
          overflowX: "auto",
          overflowY: "hidden",
          border: "1px solid rgba(255,255,255,0.08)",
          width: "100%",
          WebkitOverflowScrolling: "touch"
        }}
      >
        <table className="records-table" style={{ width: "100%", minWidth: "980px" }}>
          <thead>
            <tr>
              <th>Fecha Inicio</th>
              <th>Lote Maduración</th>
              <th>Lote Origen (Granel)</th>
              <th>Vol. Inicial</th>
              <th>Salida / Saldo</th>
              <th>Clase Obtenida (NOM-070)</th>
              <th>Inspector (V.Bo.)</th>
              <th>Estado</th>
              <th style={{ textAlign: "center" }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {maduracionRecords.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>
                  <InventoryIcon style={{ fontSize: 40, opacity: 0.4, marginBottom: "8px" }} />
                  <p style={{ margin: 0, fontSize: "14px" }}>No hay registros de maduración almacenados en el sistema.</p>
                </td>
              </tr>
            ) : (
              maduracionRecords.map((rec, i) => {
                const primerRec = (rec.recipientes && rec.recipientes[0]) || {};
                const claseDictamen = rec.clase_obtenida || rec.claseObtenida || primerRec.claseObtenida;
                const edadDictamen = rec.edad_obtenida || rec.edadObtenida || primerRec.edadObtenida;
                const tieneSalida = (parseFloat(rec.volumen_salida_total || rec.salidaVolumen || 0) > 0) || (rec.fecha_salida_maduracion || rec.salidaFecha);

                return (
                  <tr key={rec.id || rec.id_bitacora_maduracion || i}>
                    <td>{rec.fechaMovimiento || rec.fecha_inicio_maduracion}</td>
                    <td style={{ fontWeight: 800, color: "var(--agave-green)" }}>
                      {rec.noLote || rec.lote_maduracion}
                    </td>
                    <td>
                      <span style={{ color: "#3B82F6", fontWeight: 700 }}>
                        {rec.loteOrigen || rec.no_lote_origen || "-"}
                      </span>
                      {(rec.noAnalisis || rec.no_analisis || rec.clave_fq) && (
                        <div>
                          <small style={{ color: "#64748B", fontSize: "11px", fontWeight: 600 }}>
                            FQ: {rec.noAnalisis || rec.no_analisis || rec.clave_fq}
                          </small>
                        </div>
                      )}
                    </td>
                    <td style={{ fontWeight: 700 }}>
                      {rec.volumen_inicial_total || rec.invInicialVolumen || "0"} L
                    </td>
                    <td>
                      {tieneSalida ? (
                        <div>
                          <span style={{ color: "#10B981", fontWeight: 700 }}>
                            Salida: {rec.volumen_salida_total || rec.salidaVolumen || "0"} L
                            {(rec.salida_alc || rec.salidaAlc || primerRec.salida_alc || primerRec.salidaAlc) ? ` (${rec.salida_alc || rec.salidaAlc || primerRec.salida_alc || primerRec.salidaAlc}°)` : ""}
                          </span>
                          {(rec.nuevo_lote || rec.nuevoLote || primerRec.nuevo_lote || primerRec.nuevoLote) && (
                            <div style={{ marginTop: "2px" }}>
                              <span style={{ fontSize: "11px", background: "rgba(16, 185, 129, 0.12)", color: "#065F46", padding: "1px 6px", borderRadius: "4px", fontWeight: 700 }}>
                                🏷️ Nuevo Lote: {rec.nuevo_lote || rec.nuevoLote || primerRec.nuevo_lote || primerRec.nuevoLote}
                              </span>
                            </div>
                          )}
                          <br />
                          <small style={{ color: "var(--text-muted)" }}>Saldo: {rec.saldo_final_total || rec.invFinalVolumen || "0"} L</small>
                        </div>
                      ) : (
                        <span style={{ color: "#D97706", fontSize: "12px", fontWeight: 700 }}>
                          ⏳ En Maduración ({rec.volumen_inicial_total || rec.invInicialVolumen || "0"} L)
                        </span>
                      )}
                    </td>
                    <td>
                      {claseDictamen ? (
                        <div style={{ display: "inline-block", background: "rgba(217, 119, 6, 0.12)", color: "#D97706", padding: "4px 8px", borderRadius: "6px", fontWeight: 800, fontSize: "12px" }}>
                          🏆 {claseDictamen} <br />
                          <small style={{ fontWeight: 600, opacity: 0.85 }}>{edadDictamen}</small>
                        </div>
                      ) : (
                        <span style={{ fontSize: "12px", color: "var(--text-muted)", fontStyle: "italic" }}>
                          Sin fecha de salida
                        </span>
                      )}
                    </td>
                    <td style={{ fontSize: "12px", fontWeight: 600 }}>
                      {rec.inspector || rec.vbo_inspector || "Pendiente V.Bo."}
                    </td>
                    <td>
                      <span className={`badge-status ${rec.status || rec.estatus || "revision"}`}>
                        {(rec.status === "aprobado" || rec.estatus === "aprobado")
                          ? "Aprobado"
                          : (rec.status === "borrador" || rec.estatus === "borrador")
                            ? "Borrador"
                            : (rec.status === "rechazado" || rec.estatus === "rechazado")
                              ? "Rechazado"
                              : "En revisión"}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <div style={{ display: "flex", justifyContent: "center", gap: "6px" }}>
                        <button
                          type="button"
                          onClick={() => setSelectedRecord(rec)}
                          title="Ver Detalle Completo"
                          style={{ background: "rgba(52, 152, 219, 0.15)", color: "#3498db", border: "1px solid rgba(52, 152, 219, 0.3)", borderRadius: "6px", padding: "5px 8px", cursor: "pointer", display: "flex", alignItems: "center" }}
                        >
                          <VisibilityIcon style={{ fontSize: 16 }} />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleOpenSalidaModal(rec)}
                          title="Dar Salida de Maduración"
                          style={{
                            background: "rgba(217, 119, 6, 0.15)",
                            color: "#D97706",
                            border: "1px solid rgba(217, 119, 6, 0.35)",
                            borderRadius: "6px",
                            padding: "5px 8px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center"
                          }}
                        >
                          <HourglassEmptyIcon style={{ fontSize: 16 }} />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleEditRecord(rec)}
                          disabled={rec.status !== "borrador" && rec.status !== "rechazado" && rec.estatus !== "borrador" && rec.estatus !== "rechazado"}
                          title={rec.status === "borrador" || rec.status === "rechazado" || rec.estatus === "borrador" || rec.estatus === "rechazado" ? "Editar Registro" : "Solo editables en estado Borrador o Rechazado"}
                          style={{
                            background: rec.status === "borrador" || rec.status === "rechazado" || rec.estatus === "borrador" || rec.estatus === "rechazado" ? "rgba(241, 196, 15, 0.15)" : "rgba(255,255,255,0.04)",
                            color: rec.status === "borrador" || rec.status === "rechazado" || rec.estatus === "borrador" || rec.estatus === "rechazado" ? "#f1c40f" : "var(--text-muted)",
                            border: rec.status === "borrador" || rec.status === "rechazado" || rec.estatus === "borrador" || rec.estatus === "rechazado" ? "1px solid rgba(241, 196, 15, 0.3)" : "1px solid rgba(255,255,255,0.08)",
                            borderRadius: "6px",
                            padding: "5px 8px",
                            cursor: rec.status === "borrador" || rec.status === "rechazado" || rec.estatus === "borrador" || rec.estatus === "rechazado" ? "pointer" : "not-allowed",
                            display: "flex",
                            alignItems: "center"
                          }}
                        >
                          <EditIcon style={{ fontSize: 16 }} />
                        </button>

                        {handleDeleteMaduracion && (
                          <button
                            type="button"
                            onClick={() => handleDeleteMaduracion(rec)}
                            disabled={rec.status !== "borrador" && rec.status !== "rechazado" && rec.estatus !== "borrador" && rec.estatus !== "rechazado"}
                            title={rec.status === "borrador" || rec.status === "rechazado" || rec.estatus === "borrador" || rec.estatus === "rechazado" ? "Eliminar Registro" : "Solo eliminables en estado Borrador o Rechazado"}
                            style={{
                              background: rec.status === "borrador" || rec.status === "rechazado" || rec.estatus === "borrador" || rec.estatus === "rechazado" ? "rgba(239, 68, 68, 0.15)" : "rgba(255,255,255,0.04)",
                              color: rec.status === "borrador" || rec.status === "rechazado" || rec.estatus === "borrador" || rec.estatus === "rechazado" ? "#ef4444" : "var(--text-muted)",
                              border: rec.status === "borrador" || rec.status === "rechazado" || rec.estatus === "borrador" || rec.estatus === "rechazado" ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid rgba(255,255,255,0.08)",
                              borderRadius: "6px",
                              padding: "5px 8px",
                              cursor: rec.status === "borrador" || rec.status === "rechazado" || rec.estatus === "borrador" || rec.estatus === "rechazado" ? "pointer" : "not-allowed",
                              display: "flex",
                              alignItems: "center"
                            }}
                          >
                            <DeleteIcon style={{ fontSize: 16 }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* =========================================================================
          MODAL 1: FORMULARIO NUEVO REGISTRO / EDICIÓN SECCIÓN VII
      ========================================================================= */}
      {isFormOpen && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setIsFormOpen(false)}>
          <div
            className="modal-dialog-custom modal-xl animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "1020px" }}
          >
            {/* Header del Modal */}
            <div className="modal-header-custom header-theme-maduracion">
              <div className="modal-header-title">
                <div className="icon-badge">
                  <InventoryIcon />
                </div>
                <div>
                  <h3>
                    {formData.dbId || (formData.id && maduracionRecords.some(r => r.id === formData.id))
                      ? "Editar Registro de Maduración"
                      : "Nuevo Registro de Maduración (Sección VII)"}
                  </h3>
                  <p>Control de añejamiento en barricas de madera y recipientes de vidrio conforme a NOM-070</p>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setIsFormOpen(false)}
                title="Cerrar ventana"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Formulario en Modal */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmitForm("revision");
              }}
              className="modal-body-scrollable"
              style={{ display: "flex", flexDirection: "column", gap: "16px", background: "#F8FAFC" }}
            >
              {/* PASO 1: SELECCION DE LOTE Y DATOS GENERALES */}
              <section style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                <header style={{ marginBottom: "14px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ width: "34px", height: "34px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(217, 119, 6, 0.12)", color: "#D97706" }}>
                    <LayersIcon style={{ fontSize: 20 }} />
                  </div>
                  <div>
                    <h4 style={{ fontSize: "14px", fontWeight: 800, margin: 0, color: "#0F172A", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                      Paso 1: Identificación y Selección del Lote Origen (Granel)
                    </h4>
                    <p style={{ margin: "2px 0 0 0", color: "#64748B", fontSize: "12px" }}>
                      Seleccione el lote desde Graneles (Sección V) para vincular trazabilidad y descontar el volumen ingresado
                    </p>
                  </div>
                </header>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px" }}>
                  <div className="integrated-input-box">
                    <label className="integrated-input-label">FECHA INICIO MADURACIÓN *</label>
                    <input
                      type="date"
                      name="fechaMovimiento"
                      required
                      value={formData?.fechaMovimiento || today}
                      onChange={handleChange}
                      className="integrated-input-control"
                    />
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">Nº LOTE ORIGEN (GRANEL) *</label>
                    <select
                      name="loteOrigen"
                      value={(() => {
                        const sel = (granelRecords || []).find(g => {
                          const gId = String(g.id !== undefined && g.id !== null ? g.id : "").trim();
                          const gDbId = String(g.id_bitacora_granel !== undefined && g.id_bitacora_granel !== null ? g.id_bitacora_granel : (g.dbId || "")).trim();
                          const gLote = String(g.noLote || g.no_lote || "").trim();
                          const gCont = String(g.idContenedor || g.no_tanque || "").trim();
                          const targetId = String(formData?.granelOrigenId || formData?.id_bitacora_granel || "").trim();
                          const targetLote = String(formData?.loteOrigen || "").trim();

                          if (targetId && (gId === targetId || gDbId === targetId)) return true;
                          if (targetLote && (gLote === targetLote || gId === targetLote || gCont === targetLote)) return true;
                          return false;
                        });
                        return sel ? String(sel.id || sel.id_bitacora_granel || sel.noLote || sel.idContenedor) : (formData?.granelOrigenId ? String(formData.granelOrigenId) : (formData?.loteOrigen || ""));
                      })()}
                      onChange={handleLoteSelect}
                      className="integrated-select-control"
                    >
                      <option value="">-- Seleccionar Lote Origen Granel (Secc. V) --</option>
                      {(granelRecords || []).map((r, idx) => {
                        const entVol = parseFloat(r.entradaVolumen || r.invInicialVol || r.litros || 0);
                        const salVol = parseFloat(r.salidaVolumen || 0);
                        const volDisp = r.invFinalVol !== undefined && r.invFinalVol !== "" ? parseFloat(r.invFinalVol) : (r.litros !== undefined ? parseFloat(r.litros) : Math.max(0, entVol - salVol));
                        const fq = r.clave_fq || r.claveFQ || r.noAnalisis || r.noAnalisisFisicoquimico || r.no_analisis || "";
                        const maguey = r.tipoMaguey || r.especie || r.tipo_maguey || "";
                        const optVal = String(r.id || r.id_bitacora_granel || r.noLote || r.idContenedor || idx);
                        const loteName = r.noLote || r.idContenedor || `Granel #${r.id || (idx + 1)}`;
                        return (
                          <option key={`g-${idx}`} value={optVal}>
                            📦 Lote: {loteName} - {r.categoria || 'Mezcal'} ({r.clase || 'Blanco Joven'}){maguey ? ` | Maguey: ${maguey}` : ''}{fq ? ` | FQ: ${fq}` : ''} (Disp: {volDisp} L)
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">Nº LOTE DE MADURACIÓN *</label>
                    <input
                      type="text"
                      name="noLote"
                      required
                      placeholder="Ej. MAD-ESP-2026-01"
                      value={formData?.noLote || ""}
                      onChange={handleChange}
                      className="integrated-input-control"
                    />
                  </div>
                </div>

                {/* BADGE DE INFORMACION DE GRANEL SELECCIONADO */}
                {(formData.granelOrigenId || formData.loteOrigen) && (
                  <div style={{ border: "1px solid rgba(46, 204, 113, 0.4)", background: "rgba(46, 204, 113, 0.05)", padding: "14px 18px", borderRadius: "10px", marginTop: "12px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "12px", fontSize: "12.5px" }}>
                    <div>
                      <span style={{ color: "var(--text-muted)", fontSize: "11px", display: "block", textTransform: "uppercase", fontWeight: 600 }}>Lote Granel Origen:</span>
                      <strong style={{ color: "#2ecc71", fontSize: "13.5px" }}>{formData.loteOrigen || formData.noLote}</strong>
                    </div>
                    <div>
                      <span style={{ color: "var(--text-muted)", fontSize: "11px", display: "block", textTransform: "uppercase", fontWeight: 600 }}>Volumen Disponible:</span>
                      <strong style={{ color: "var(--agave-green)", fontSize: "13.5px" }}>{formData.granelVolDisponible !== undefined ? `${formData.granelVolDisponible} Litros` : "0 Litros"}</strong>
                    </div>
                    <div>
                      <span style={{ color: "var(--text-muted)", fontSize: "11px", display: "block", textTransform: "uppercase", fontWeight: 600 }}>Graduación Alcohólica:</span>
                      <strong style={{ color: "#f39c12", fontSize: "13.5px" }}>{formData.granelAlcGrad ? `${formData.granelAlcGrad}% Alc. Vol.` : "Sin registrar"}</strong>
                    </div>
                    <div>
                      <span style={{ color: "var(--text-muted)", fontSize: "11px", display: "block", textTransform: "uppercase", fontWeight: 600 }}>Categoría / Clase Origen:</span>
                      <strong style={{ color: "#0F172A", fontSize: "13px" }}>{formData.categoria || "Mezcal Artesanal"} ({formData.claseOrigen || formData.clase || "Blanco Joven"})</strong>
                    </div>
                    <div>
                      <span style={{ color: "var(--text-muted)", fontSize: "11px", display: "block", textTransform: "uppercase", fontWeight: 600 }}>Tipo de Maguey(es):</span>
                      <strong style={{ color: "#16a34a", fontSize: "13px" }}>{formData.tipoMaguey || "No especificado"}</strong>
                    </div>
                    <div>
                      <span style={{ color: "var(--text-muted)", fontSize: "11px", display: "block", textTransform: "uppercase", fontWeight: 600 }}>Nº Análisis FQ:</span>
                      <strong style={{ color: formData.noAnalisis ? "#2563eb" : "#94a3b8", fontSize: "13px" }}>
                        {formData.noAnalisis || "Sin análisis registrado"}
                      </strong>
                    </div>
                  </div>
                )}

                {superaSaldoGranel && (
                  <div style={{ background: "rgba(231, 76, 60, 0.15)", border: "1px solid rgba(231, 76, 60, 0.4)", padding: "10px 14px", borderRadius: "8px", marginTop: "10px", fontSize: "13px", color: "#e74c3c" }}>
                    ⚠️ <strong>Atención:</strong> El volumen total a madurar (<strong>{totalVolumenMadurar} L</strong>) supera el saldo disponible en Graneles (<strong>{formData.granelVolDisponible} L</strong>).
                  </div>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "10px", marginTop: "10px" }}>
                  <div className="integrated-input-box">
                    <label className="integrated-input-label">CATEGORÍA</label>
                    <select
                      name="categoria"
                      value={formData?.categoria || "Mezcal Artesanal"}
                      onChange={handleChange}
                      className="integrated-select-control"
                    >
                      <option value="Mezcal">Mezcal</option>
                      <option value="Mezcal Artesanal">Mezcal Artesanal</option>
                      <option value="Mezcal Ancestral">Mezcal Ancestral</option>
                    </select>
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">CLASE DE ORIGEN</label>
                    <input
                      type="text"
                      name="claseOrigen"
                      value={formData?.claseOrigen || formData?.clase || "Blanco Joven"}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData(prev => ({ ...prev, claseOrigen: val, clase: val }));
                      }}
                      className="integrated-input-control"
                    />
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">TIPO DE MAGUEY(ES)</label>
                    <input
                      type="text"
                      name="tipoMaguey"
                      value={formData?.tipoMaguey || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData(prev => ({ ...prev, tipoMaguey: val, especie: val }));
                      }}
                      placeholder="Ej. Espadín"
                      className="integrated-input-control"
                    />
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">Nº ANÁLISIS FISICOQUÍMICO (FQ)</label>
                    <input
                      type="text"
                      name="noAnalisis"
                      value={formData?.noAnalisis || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData(prev => ({ ...prev, noAnalisis: val, no_analisis: val, clave_fq: val }));
                      }}
                      placeholder="Ej. ANA-2026-098 (dejar en blanco si no tiene)"
                      className="integrated-input-control"
                    />
                  </div>
                </div>
              </section>

              {/* PASO 2: RECIPIENTES DE MADURACIÓN DINÁMICOS */}
              <section style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div style={{ width: "34px", height: "34px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(210, 105, 30, 0.12)", color: "var(--copper)" }}>
                      <ScienceIcon style={{ fontSize: 20 }} />
                    </div>
                    <div>
                      <h4 style={{ fontSize: "14px", fontWeight: 800, margin: 0, color: "#0F172A", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        Paso 2: Recipientes, Entradas y Salidas Diferidas
                      </h4>
                      <p style={{ margin: "2px 0 0 0", color: "#64748B", fontSize: "12px" }}>
                        Registre entradas e inventario inicial ahora. Las salidas, mermas e inventario final pueden dejarse vacíos y registrarse en cualquier momento posterior.
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleAddRecipiente}
                    className="btn-add-new"
                    style={{ background: "rgba(46, 204, 113, 0.15)", color: "#2ecc71", border: "1px solid rgba(46, 204, 113, 0.4)", padding: "7px 14px", borderRadius: "8px", fontSize: "12.5px" }}
                  >
                    <AddIcon style={{ fontSize: 17 }} />
                    <span style={{ fontWeight: 700 }}>+ Agregar Recipiente</span>
                  </button>
                </header>

                {recipientes.map((recItem, idx) => {
                  const fechaIni = recItem.invInicialFecha || formData?.fechaMovimiento || today;
                  const fechaSal = recItem.salidaFecha;
                  const normaEval = evaluarNormaClase(recItem.tipoRecipiente, fechaIni, fechaSal);

                  return (
                    <div
                      key={recItem.id || idx}
                      style={{
                        background: "#F8FAFC",
                        border: "1.5px solid #CBD5E1",
                        borderRadius: "14px",
                        padding: "16px",
                        marginBottom: "16px"
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                        <span style={{ fontWeight: 800, fontSize: "13px", color: "var(--copper)" }}>
                          Contenedor #{idx + 1}
                        </span>
                        {recipientes.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveRecipiente(recItem.id)}
                            style={{ background: "transparent", border: "none", color: "#EF4444", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: 700 }}
                          >
                            <DeleteIcon style={{ fontSize: 16 }} /> Quitar
                          </button>
                        )}
                      </div>

                      {/* DATOS DEL RECIPIENTE */}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "10px", marginBottom: "12px" }}>
                        <div className="integrated-input-box">
                          <label className="integrated-input-label">CANTIDAD DE RECIPIENTE *</label>
                          <input
                            type="text"
                            value={recItem.num_recipiente || recItem.noRecipiente || ``}
                            onChange={e => handleRecipienteChange(recItem.id, "num_recipiente", e.target.value)}
                            className="integrated-input-control"
                            placeholder="Ej. B-01, G-01"
                          />
                        </div>

                        <div className="integrated-input-box">
                          <label className="integrated-input-label">TIPO DE RECIPIENTE *</label>
                          <select
                            value={recItem.tipoRecipiente || recItem.tipo_recipiente || "BARRICAS DE MADERA DE"}
                            onChange={e => handleRecipienteChange(recItem.id, "tipoRecipiente", e.target.value)}
                            className="integrated-select-control"
                            style={{ fontWeight: 600 }}
                          >
                            <option value="BARRICAS DE MADERA DE">BARRICAS DE MADERA</option>
                            <option value="PIPONES DE MADERA">PIPONES DE MADERA</option>
                            <option value="TONELES DE MADERA">TONELES DE MADERA</option>
                            <option value="GARRAFAS DE VIDRIO">GARRAFAS DE VIDRIO</option>
                            <option value="DAMAJUANAS DE VIDRIO">DAMAJUANAS DE VIDRIO</option>
                          </select>
                        </div>

                        <div className="integrated-input-box">
                          <label className="integrated-input-label">DETALLE / CAPACIDAD / TOSTADO</label>
                          <input
                            type="text"
                            value={recItem.detalleRecipiente || recItem.detalle_recipiente || ""}
                            onChange={e => handleRecipienteChange(recItem.id, "detalleRecipiente", e.target.value)}
                            className="integrated-input-control"
                            placeholder="Ej. Roble Francés 200L / Garrafa Vidrio 20L"
                          />
                        </div>
                      </div>

                      {/* FASE 1: INVENTARIO INICIAL & ENTRADAS */}
                      <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "12px", marginBottom: "12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                          <HourglassEmptyIcon style={{ fontSize: 16, color: "var(--copper)" }} />
                          <span style={{ fontSize: "11.5px", color: "var(--copper)", textTransform: "uppercase", letterSpacing: "0.4px", fontWeight: 800 }}>
                            Fase 1: Entrada e Inventario Inicial
                          </span>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "8px" }}>
                          <div className="integrated-input-box">
                            <label className="integrated-input-label">FECHA INGRESO *</label>
                            <input
                              type="date"
                              value={recItem.invInicialFecha || formData?.fechaMovimiento || today}
                              onChange={e => handleRecipienteChange(recItem.id, "invInicialFecha", e.target.value)}
                              className="integrated-input-control"
                            />
                          </div>

                          <div className="integrated-input-box">
                            <label className="integrated-input-label">VOLUMEN INICIAL (L) *</label>
                            <input
                              type="number"
                              placeholder="Ej. 100"
                              value={recItem.invInicialVolumen || ""}
                              onChange={e => handleRecipienteChange(recItem.id, "invInicialVolumen", e.target.value)}
                              className="integrated-input-control"
                              style={{ paddingRight: "36px" }}
                            />
                            <span className="integrated-input-suffix">L</span>
                          </div>

                          <div className="integrated-input-box">
                            <label className="integrated-input-label">% ALC. VOL.</label>
                            <input
                              type="number"
                              step="0.1"
                              placeholder="Ej. 47.0"
                              value={recItem.invInicialAlc || formData?.granelAlcGrad || ""}
                              onChange={e => handleRecipienteChange(recItem.id, "invInicialAlc", e.target.value)}
                              className="integrated-input-control"
                              style={{ paddingRight: "36px" }}
                            />
                            <span className="integrated-input-suffix">%</span>
                          </div>

                          <div className="integrated-input-box">
                            <label className="integrated-input-label">VOLUMEN ENTRADA ADICIONAL</label>
                            <input
                              type="number"
                              value={recItem.entradaVolumen || "0"}
                              onChange={e => handleRecipienteChange(recItem.id, "entradaVolumen", e.target.value)}
                              className="integrated-input-control"
                              style={{ paddingRight: "36px" }}
                            />
                            <span className="integrated-input-suffix">L</span>
                          </div>
                        </div>
                      </div>

                      {/* FASE 2: SALIDAS, MERMAS, DICTAMEN NOM-070 Y SALDO RESTANTE */}
                      <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "12px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <HourglassFullIcon style={{ fontSize: 16, color: "#10B981" }} />
                            <span style={{ fontSize: "11.5px", color: "#10B981", textTransform: "uppercase", letterSpacing: "0.4px", fontWeight: 800 }}>
                              Fase 2: Salidas, Mermas e Inventario Final (Diferido / Opcional)
                            </span>
                          </div>
                          {!recItem.salidaFecha && (
                            <span style={{ fontSize: "11px", background: "rgba(217, 119, 6, 0.1)", color: "#D97706", padding: "2px 8px", borderRadius: "4px", fontWeight: 700 }}>
                              ⏳ En Maduración Activa
                            </span>
                          )}
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "8px" }}>
                          <div className="integrated-input-box">
                            <label className="integrated-input-label">FECHA DE SALIDA (OPCIONAL)</label>
                            <input
                              type="date"
                              value={recItem.salidaFecha || ""}
                              onChange={e => handleRecipienteChange(recItem.id, "salidaFecha", e.target.value)}
                              className="integrated-input-control"
                            />
                          </div>

                          <div className="integrated-input-box">
                            <label className="integrated-input-label">VOLUMEN SALIDA / EXTRACCIÓN</label>
                            <input
                              type="number"
                              placeholder="Ej. 50 (parcial o total)"
                              value={recItem.salidaVolumen || ""}
                              onChange={e => handleRecipienteChange(recItem.id, "salidaVolumen", e.target.value)}
                              className="integrated-input-control"
                              style={{ paddingRight: "36px" }}
                            />
                            <span className="integrated-input-suffix">L</span>
                          </div>

                          <div className="integrated-input-box">
                            <label className="integrated-input-label">% ALC. VOL. SALIDA</label>
                            <input
                              type="number"
                              step="0.1"
                              placeholder="Ej. 45.0"
                              value={recItem.salidaAlc || ""}
                              onChange={e => handleRecipienteChange(recItem.id, "salidaAlc", e.target.value)}
                              className="integrated-input-control"
                              style={{ paddingRight: "36px" }}
                            />
                            <span className="integrated-input-suffix">%</span>
                          </div>

                          <div className="integrated-input-box">
                            <label className="integrated-input-label">VOLUMEN DE MERMA</label>
                            <input
                              type="number"
                              placeholder="Ej. 2"
                              value={recItem.salidaMerma || ""}
                              onChange={e => handleRecipienteChange(recItem.id, "salidaMerma", e.target.value)}
                              className="integrated-input-control"
                              style={{ paddingRight: "36px" }}
                            />
                            <span className="integrated-input-suffix">L</span>
                          </div>

                          <div className="integrated-input-box" style={{ background: "rgba(74, 93, 35, 0.05)" }}>
                            <label className="integrated-input-label">SALDO RESTANTE FINAL *</label>
                            <input
                              type="number"
                              value={recItem.invFinalVolumen || ""}
                              onChange={e => handleRecipienteChange(recItem.id, "invFinalVolumen", e.target.value)}
                              className="integrated-input-control"
                              style={{ fontWeight: 800, color: "var(--agave-green)", paddingRight: "36px" }}
                            />
                            <span className="integrated-input-suffix">L</span>
                          </div>

                          <div className="integrated-input-box" style={{ background: "rgba(16, 185, 129, 0.05)", border: "1px solid rgba(16, 185, 129, 0.35)" }}>
                            <label className="integrated-input-label" style={{ color: "#065F46", fontWeight: 800 }}>
                              ✨ NUEVO LOTE OBTENIDO (PARA GRANEL)
                            </label>
                            <input
                              type="text"
                              placeholder="Ej. AÑE-LOTE-01 / MAD-LOTE-01"
                              value={recItem.nuevoLote || recItem.nuevo_lote || ""}
                              onChange={e => handleRecipienteChange(recItem.id, "nuevoLote", e.target.value)}
                              className="integrated-input-control"
                              style={{ fontWeight: 700, color: "#065F46" }}
                            />
                          </div>
                        </div>

                        {/* DICTAMEN NOM-070 EN VIVO */}
                        {normaEval.evaluada ? (
                          <div style={{ marginTop: "10px", padding: "10px 14px", borderRadius: "8px", background: "rgba(16, 185, 129, 0.08)", border: "1px solid rgba(16, 185, 129, 0.3)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                              <strong style={{ color: "#065F46", fontSize: "13px" }}>
                                🏆 Dictamen NOM-070: {normaEval.clase} ({normaEval.edad})
                              </strong>
                              <p style={{ margin: "2px 0 0 0", color: "#047857", fontSize: "11.5px" }}>
                                {normaEval.mensaje}
                              </p>
                            </div>
                            <span style={{ background: "#10B981", color: "white", padding: "4px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: 800 }}>
                              {normaEval.clase}
                            </span>
                          </div>
                        ) : (
                          <div style={{ marginTop: "10px", padding: "8px 12px", borderRadius: "8px", background: "rgba(245, 158, 11, 0.08)", border: "1px solid rgba(245, 158, 11, 0.2)", fontSize: "12px", color: "#B45309", display: "flex", alignItems: "center", gap: "6px" }}>
                            <InfoOutlinedIcon style={{ fontSize: 16 }} />
                            <span>
                              {recItem.salidaFecha ? normaEval.mensaje : "Lote en proceso de maduración. Las salidas y evaluación NOM-070 quedan libres para completarse en un momento posterior."}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </section>

              {/* PASO 3: OBSERVACIONES Y FINALIZACION */}
              <section style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                <header style={{ marginBottom: "14px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ width: "34px", height: "34px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(74, 93, 35, 0.12)", color: "var(--agave-green)" }}>
                    <SettingsIcon style={{ fontSize: 20 }} />
                  </div>
                  <div>
                    <h4 style={{ fontSize: "14px", fontWeight: 800, margin: 0, color: "#0F172A", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                      Paso 3: Observaciones y Finalización
                    </h4>
                    <p style={{ margin: "2px 0 0 0", color: "#64748B", fontSize: "12px" }}>
                      Comentarios del estado de barricas, tostado o notas de bodega
                    </p>
                  </div>
                </header>

                <div className="integrated-input-box" style={{ minHeight: "75px", justifyContent: "flex-start", padding: "8px 14px" }}>
                  <label className="integrated-input-label">OBSERVACIONES GENERALES</label>
                  <textarea
                    name="observaciones"
                    value={formData?.observaciones || ""}
                    onChange={handleChange}
                    className="integrated-input-control"
                    style={{ height: "42px", resize: "none" }}
                    placeholder="Escriba aquí notas generales sobre el proceso de maduración..."
                  />
                </div>
              </section>

              {/* Botones del Modal */}
              <div className="modal-footer-custom" style={{ marginTop: "6px", borderRadius: "12px", background: "#FFFFFF", padding: "14px 20px" }}>
                <button
                  type="button"
                  className="btn-cancel-modal"
                  onClick={() => setIsFormOpen(false)}
                >
                  Cancelar
                </button>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    className="btn-secondary-custom"
                    style={{ padding: "9px 18px", fontSize: "12.5px" }}
                    onClick={() => handleSubmitForm("borrador")}
                  >
                    Guardar Borrador
                  </button>
                  <button
                    type="submit"
                    className="btn-submit-modal"
                    disabled={superaSaldoGranel}
                    style={{ background: "linear-gradient(135deg, #D97706 0%, #92400E 100%)", boxShadow: "0 4px 14px rgba(217, 119, 6, 0.35)", opacity: superaSaldoGranel ? 0.5 : 1, cursor: superaSaldoGranel ? "not-allowed" : "pointer" }}
                  >
                    <CheckCircleIcon style={{ fontSize: 18 }} />
                    <span>{formData.dbId || (formData.id && maduracionRecords.some(r => r.id === formData.id)) ? "Guardar Cambios" : "Registrar Sección VII"}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          MODAL 2: DETALLE COMPLETO DE MADURACIÓN
      ========================================================================= */}
      {selectedRecord && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setSelectedRecord(null)}>
          <div
            className="detail-modal-card animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "860px" }}
          >
            {/* Header del Modal */}
            <div className="detail-modal-header header-theme-maduracion">
              <div className="modal-header-title">
                <div className="icon-badge">
                  <InventoryIcon />
                </div>
                <div>
                  <h3 style={{ margin: 0, color: "white" }}>
                    Lote Madurado: {selectedRecord.noLote || selectedRecord.lote_maduracion}
                  </h3>
                  <p style={{ margin: "2px 0 0 0", color: "rgba(255,255,255,0.85)", fontSize: "12px" }}>
                    Detalle de Control de Maduración de Mezcal (Sección VII)
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setSelectedRecord(null)}
              >
                <CloseIcon />
              </button>
            </div>

            {/* Contenido del Detalle */}
            <div className="detail-modal-body" style={{ padding: "24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", paddingBottom: "12px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                <div>
                  <span style={{ color: "var(--text-muted)", fontSize: "12px", display: "block" }}>Categoría y Clase Dictaminada:</span>
                  <span style={{ color: "var(--copper)", fontSize: "16px", fontWeight: 800 }}>
                    {selectedRecord.categoria} — {selectedRecord.clase_obtenida || selectedRecord.claseObtenida || selectedRecord.clase || "En Maduración"}
                  </span>
                  {(selectedRecord.edad_obtenida || selectedRecord.edadObtenida) && (
                    <span style={{ display: "block", fontSize: "12.5px", color: "var(--agave-green)", fontWeight: 700 }}>
                      Edad: {selectedRecord.edad_obtenida || selectedRecord.edadObtenida}
                    </span>
                  )}
                </div>
                <span className={`badge-status ${selectedRecord.status || selectedRecord.estatus || "revision"}`}>
                  {(selectedRecord.status === "aprobado" || selectedRecord.estatus === "aprobado") ? "Aprobado" : (selectedRecord.status === "rechazado" || selectedRecord.estatus === "rechazado") ? "Rechazado" : (selectedRecord.status === "borrador" || selectedRecord.estatus === "borrador") ? "Borrador" : "En revisión"}
                </span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", fontSize: "13.5px" }}>
                <div><strong>Fecha de Inicio:</strong> {selectedRecord.fechaMovimiento || selectedRecord.fecha_inicio_maduracion}</div>
                <div><strong>Lote Maduración:</strong> <span style={{ color: "var(--agave-green)", fontWeight: 700 }}>{selectedRecord.noLote || selectedRecord.lote_maduracion}</span></div>
                <div><strong>Lote Origen (Granel):</strong> <span style={{ color: "#3B82F6", fontWeight: 700 }}>{selectedRecord.loteOrigen || selectedRecord.no_lote_origen || "N/A"}</span></div>
                <div><strong>Tipo de Maguey:</strong> {selectedRecord.tipoMaguey || selectedRecord.tipo_maguey || "N/A"}</div>
                <div><strong>Nº Análisis Fisicoquímico:</strong> {selectedRecord.noAnalisis || selectedRecord.no_analisis || "N/A"}</div>
                <div><strong>Volumen Inicial Total:</strong> {selectedRecord.volumen_inicial_total || selectedRecord.invInicialVolumen || "0"} L</div>
                <div><strong>Volumen Salida Total:</strong> {selectedRecord.volumen_salida_total || selectedRecord.salidaVolumen || "0"} L</div>
                <div><strong>% Alc. Vol. Salida:</strong> {(selectedRecord.salida_alc || selectedRecord.salidaAlc || (selectedRecord.recipientes && selectedRecord.recipientes[0] && (selectedRecord.recipientes[0].salida_alc || selectedRecord.recipientes[0].salidaAlc)) || "-")} %</div>
                <div><strong>Saldo Restante en Bodega:</strong> {selectedRecord.saldo_final_total || selectedRecord.invFinalVolumen || "0"} L</div>
                {(selectedRecord.nuevo_lote || selectedRecord.nuevoLote) && (
                  <div style={{ gridColumn: "1 / -1", background: "rgba(16, 185, 129, 0.08)", padding: "8px 12px", borderRadius: "8px", border: "1px solid rgba(16, 185, 129, 0.25)" }}>
                    <strong style={{ color: "#065F46" }}>🏷️ Nuevo Lote Obtenido (Granel):</strong> <span style={{ color: "#065F46", fontWeight: 800 }}>{selectedRecord.nuevo_lote || selectedRecord.nuevoLote}</span>
                  </div>
                )}
                <div style={{ gridColumn: "1 / -1" }}><strong>Inspector Responsable:</strong> {selectedRecord.inspector || selectedRecord.vbo_inspector || "Pendiente V.Bo."}</div>
              </div>

              {/* TABLA DE RECIPIENTES ASOCIADOS */}
              {selectedRecord.recipientes && selectedRecord.recipientes.length > 0 && (
                <div style={{ marginTop: "18px", paddingTop: "14px", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                  <h4 style={{ fontSize: "13px", fontWeight: 800, textTransform: "uppercase", color: "#334155", marginBottom: "8px" }}>
                    Recipientes y Barricas Registradas
                  </h4>
                  <table style={{ width: "100%", fontSize: "12px", borderCollapse: "collapse", border: "1px solid #E2E8F0" }}>
                    <thead>
                      <tr style={{ background: "#F1F5F9", textAlign: "left" }}>
                        <th style={{ padding: "6px 8px" }}>Nº</th>
                        <th style={{ padding: "6px 8px" }}>Tipo</th>
                        <th style={{ padding: "6px 8px" }}>Detalle</th>
                        <th style={{ padding: "6px 8px" }}>V. Inicial</th>
                        <th style={{ padding: "6px 8px" }}>Salida</th>
                        <th style={{ padding: "6px 8px" }}>Nuevo Lote</th>
                        <th style={{ padding: "6px 8px" }}>Saldo</th>
                        <th style={{ padding: "6px 8px" }}>Clase / Edad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRecord.recipientes.map((r, idx) => (
                        <tr key={idx} style={{ borderBottom: "1px solid #E2E8F0" }}>
                          <td style={{ padding: "6px 8px", fontWeight: 700 }}>{r.num_recipiente || r.noRecipiente || (idx + 1)}</td>
                          <td style={{ padding: "6px 8px" }}>{r.tipo_recipiente || r.tipoRecipiente}</td>
                          <td style={{ padding: "6px 8px" }}>{r.detalle_recipiente || r.detalleRecipiente || "-"}</td>
                          <td style={{ padding: "6px 8px" }}>{r.inv_inicial_volumen || r.invInicialVolumen} L</td>
                          <td style={{ padding: "6px 8px", color: "#10B981", fontWeight: 700 }}>
                            {r.salida_volumen || r.salidaVolumen || 0} L {(r.salida_alc || r.salidaAlc) ? `(${r.salida_alc || r.salidaAlc}°)` : ""} {r.salida_fecha ? `[${r.salida_fecha}]` : ""}
                          </td>
                          <td style={{ padding: "6px 8px", fontWeight: 700, color: "#065F46" }}>
                            {r.nuevo_lote || r.nuevoLote || selectedRecord.nuevo_lote || selectedRecord.nuevoLote || "-"}
                          </td>
                          <td style={{ padding: "6px 8px", fontWeight: 700, color: "var(--agave-green)" }}>
                            {r.inv_final_volumen || r.invFinalVolumen || 0} L
                          </td>
                          <td style={{ padding: "6px 8px" }}>
                            {r.clase_obtenida || r.claseObtenida ? (
                              <span style={{ color: "#D97706", fontWeight: 700 }}>
                                {r.clase_obtenida || r.claseObtenida} ({r.edad_obtenida || r.edadObtenida})
                              </span>
                            ) : (
                              <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>En Maduración</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedRecord.observaciones && (
                <div style={{ marginTop: "18px", paddingTop: "14px", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                  <strong>Observaciones:</strong>
                  <p style={{ margin: "6px 0 0 0", color: "#64748B", fontSize: "13px" }}>
                    {selectedRecord.observaciones}
                  </p>
                </div>
              )}

              {selectedRecord.observaciones_correccion && (
                <div style={{ marginTop: "12px", padding: "10px 14px", background: "rgba(239, 68, 68, 0.08)", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "8px" }}>
                  <strong style={{ color: "#DC2626" }}>Observaciones de Corrección del Inspector:</strong>
                  <p style={{ margin: "4px 0 0 0", color: "#B91C1C", fontSize: "13px" }}>
                    {selectedRecord.observaciones_correccion}
                  </p>
                </div>
              )}

              <div className="modal-footer-custom" style={{ marginTop: "20px", borderRadius: "10px" }}>
                <button
                  type="button"
                  onClick={() => setSelectedRecord(null)}
                  className="btn-cancel-modal"
                >
                  Cerrar
                </button>
                {(selectedRecord.status === "borrador" || selectedRecord.status === "rechazado" || selectedRecord.estatus === "borrador" || selectedRecord.estatus === "rechazado") && (
                  <button
                    type="button"
                    onClick={() => {
                      const rec = selectedRecord;
                      setSelectedRecord(null);
                      handleEditRecord(rec);
                    }}
                    className="btn-submit-modal"
                    style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", boxShadow: "0 4px 14px rgba(245, 158, 11, 0.3)" }}
                  >
                    <EditIcon style={{ fontSize: 16 }} />
                    <span>Editar Registro</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          MODAL 3: SALIDA DE PRODUCTO DE MADURACIÓN
      ========================================================================= */}
      {isSalidaModalOpen && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setIsSalidaModalOpen(false)}>
          <div
            className="modal-dialog-custom modal-lg animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "920px" }}
          >
            {/* Header del Modal */}
            <div
              className="modal-header-custom"
              style={{
                background: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
                borderBottom: "3px solid #D97706",
                padding: "18px 24px"
              }}
            >
              <div className="modal-header-title">
                <div
                  className="icon-badge"
                  style={{
                    background: "rgba(217, 119, 6, 0.2)",
                    color: "#D97706",
                    border: "1px solid rgba(217, 119, 6, 0.4)"
                  }}
                >
                  <HourglassFullIcon style={{ fontSize: 24 }} />
                </div>
                <div>
                  <h3 style={{ margin: 0, color: "#F8FAFC", fontSize: "18px", fontWeight: 800 }}>
                    Salida de Producto de Maduración
                  </h3>
                  <p style={{ margin: "3px 0 0 0", color: "#94A3B8", fontSize: "12px" }}>
                    Registro de extracción de barricas/vidrio, cálculo de edad y dictamen según NOM-070-SCFI-2016
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setIsSalidaModalOpen(false)}
                title="Cerrar ventana"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Cuerpo del Formulario */}
            <form
              onSubmit={handleSaveSalidaMaduracion}
              className="modal-body-scrollable"
              style={{
                padding: "20px 24px",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                background: "#F8FAFC"
              }}
            >
              {/* SECCIÓN 1: SELECCIONAR LOTE EN MADURACIÓN */}
              <section
                style={{
                  background: "#FFFFFF",
                  border: "1px solid #E2E8F0",
                  borderRadius: "14px",
                  padding: "16px 18px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
                }}
              >
                <header style={{ marginBottom: "14px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(217, 119, 6, 0.12)",
                      color: "#D97706"
                    }}
                  >
                    <LayersIcon style={{ fontSize: 19 }} />
                  </div>
                  <div>
                    <h4
                      style={{
                        fontSize: "13.5px",
                        fontWeight: 800,
                        margin: 0,
                        color: "#0F172A",
                        textTransform: "uppercase",
                        letterSpacing: "0.3px"
                      }}
                    >
                      1. Lote en Maduración a Extraer
                    </h4>
                    <p style={{ margin: "1px 0 0 0", color: "#64748B", fontSize: "12px" }}>
                      Seleccione la barrica o lote de maduración del cual se extraerá el mezcal
                    </p>
                  </div>
                </header>

                <div className="integrated-input-box">
                  <label className="integrated-input-label">SELECCIONE EL LOTE EN MADURACIÓN *</label>
                  <select
                    className="integrated-select-control"
                    value={salidaForm.maduracionId}
                    onChange={(e) => handleSelectLoteSalida(e.target.value)}
                    required
                    style={{ fontWeight: 700, color: "#0F172A" }}
                  >
                    <option value="">-- Seleccionar Lote de Maduración / Granel Disponible --</option>
                    {availableMaduracionSalidaLots.filter(o => o.type === "maduracion").length > 0 && (
                      <optgroup label="🏺 Lotes en Maduración (Sección VII)">
                        {availableMaduracionSalidaLots.filter(o => o.type === "maduracion").map((opt) => (
                          <option key={opt.key} value={opt.key}>
                            {opt.label}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {availableMaduracionSalidaLots.filter(o => o.type === "granel_maduracion").length > 0 && (
                      <optgroup label="📦 Lotes a Granel en Maduración / Barricas (Sección V)">
                        {availableMaduracionSalidaLots.filter(o => o.type === "granel_maduracion").map((opt) => (
                          <option key={opt.key} value={opt.key}>
                            {opt.label}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {availableMaduracionSalidaLots.filter(o => o.type === "granel_general").length > 0 && (
                      <optgroup label="📦 Otros Lotes a Granel con Saldo Disponible">
                        {availableMaduracionSalidaLots.filter(o => o.type === "granel_general").map((opt) => (
                          <option key={opt.key} value={opt.key}>
                            {opt.label}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>

                {/* Tarjeta de Resumen del Lote Seleccionado */}
                {currentSelectedLot && (
                  <div
                    style={{
                      marginTop: "12px",
                      background: "linear-gradient(135deg, rgba(217, 119, 6, 0.04) 0%, rgba(245, 158, 11, 0.06) 100%)",
                      border: "1px solid rgba(217, 119, 6, 0.25)",
                      borderRadius: "10px",
                      padding: "12px 16px",
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                      gap: "12px"
                    }}
                  >
                    <div>
                      <span style={{ fontSize: "10px", color: "#64748B", fontWeight: 800, textTransform: "uppercase", display: "block" }}>
                        LOTE MADURACIÓN
                      </span>
                      <strong style={{ color: "#D97706", fontSize: "13px", fontWeight: 800 }}>
                        {currentLoteMaduracion}
                      </strong>
                    </div>

                    <div>
                      <span style={{ fontSize: "10px", color: "#64748B", fontWeight: 800, textTransform: "uppercase", display: "block" }}>
                        LOTE ORIGEN (GRANEL)
                      </span>
                      <strong style={{ color: "#0F172A", fontSize: "12.5px", fontWeight: 700 }}>
                        {currentLoteOrigen}
                      </strong>
                    </div>

                    <div>
                      <span style={{ fontSize: "10px", color: "#64748B", fontWeight: 800, textTransform: "uppercase", display: "block" }}>
                        MAGUEY (ORIGEN)
                      </span>
                      <strong style={{ color: "#0F172A", fontSize: "12px", fontWeight: 700 }}>
                        {currentMaguey || "Maguey Espadín"}
                      </strong>
                    </div>

                    <div>
                      <span style={{ fontSize: "10px", color: "#64748B", fontWeight: 800, textTransform: "uppercase", display: "block" }}>
                        CATEGORÍA / CLASE ORIGEN
                      </span>
                      <strong style={{ color: "#0F172A", fontSize: "12px", fontWeight: 700 }}>
                        {currentCategoria} ({currentClaseOrigen})
                      </strong>
                    </div>

                    <div>
                      <span style={{ fontSize: "10px", color: "#64748B", fontWeight: 800, textTransform: "uppercase", display: "block" }}>
                        TIPO RECIPIENTE
                      </span>
                      <strong style={{ color: "#0F172A", fontSize: "12px", fontWeight: 700 }}>
                        {currentTipoRecipiente}
                      </strong>
                    </div>

                    <div>
                      <span style={{ fontSize: "10px", color: "#64748B", fontWeight: 800, textTransform: "uppercase", display: "block" }}>
                        FECHA INGRESO
                      </span>
                      <strong style={{ color: "#0F172A", fontSize: "12.5px", fontWeight: 700 }}>
                        {currentFechaInicio}
                      </strong>
                    </div>

                    <div style={{ background: "#FFFFFF", padding: "6px 12px", borderRadius: "8px", border: "1px solid #A7F3D0" }}>
                      <span style={{ fontSize: "9.5px", color: "#047857", fontWeight: 800, textTransform: "uppercase", display: "block" }}>
                        SALDO DISPONIBLE
                      </span>
                      <strong style={{ color: "#059669", fontSize: "14px", fontWeight: 900 }}>
                        {currentSaldoDisponible.toLocaleString()} L
                      </strong>
                    </div>
                  </div>
                )}
              </section>

              {/* SECCIÓN 2: FECHA DE SALIDA Y DICTAMEN NOM-070 */}
              <section
                style={{
                  background: "#FFFFFF",
                  border: "1px solid #E2E8F0",
                  borderRadius: "14px",
                  padding: "16px 18px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
                }}
              >
                <header style={{ marginBottom: "14px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(37, 99, 235, 0.12)",
                      color: "#2563EB"
                    }}
                  >
                    <ScienceIcon style={{ fontSize: 19 }} />
                  </div>
                  <div>
                    <h4
                      style={{
                        fontSize: "13.5px",
                        fontWeight: 800,
                        margin: 0,
                        color: "#0F172A",
                        textTransform: "uppercase",
                        letterSpacing: "0.3px"
                      }}
                    >
                      2. Fecha de Salida y Evaluación NOM-070
                    </h4>
                    <p style={{ margin: "1px 0 0 0", color: "#64748B", fontSize: "12px" }}>
                      Cálculo de tiempo de permanencia y determinación oficial de la clase obtenida
                    </p>
                  </div>
                </header>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1.3fr", gap: "14px", alignItems: "stretch" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <div className="integrated-input-box" style={{ flex: 1, justifyContent: "center" }}>
                      <label className="integrated-input-label">FECHA DE SALIDA / EXTRACCIÓN *</label>
                      <input
                        type="date"
                        className="integrated-input-control"
                        value={salidaForm.fechaSalida}
                        onChange={(e) => handleFechaSalidaChange(e.target.value)}
                        min={currentFechaInicio}
                        required
                        style={{ fontWeight: 700, color: "#0F172A", fontSize: "14px" }}
                      />
                    </div>
                    {currentFechaInicio && (
                      <span style={{ fontSize: "11px", color: "#64748B", paddingLeft: "4px" }}>
                        🗓️ Inicio de maduración: <strong>{currentFechaInicio}</strong>
                      </span>
                    )}
                  </div>

                  {/* Card de Dictamen Oficial NOM-070 */}
                  <div
                    style={{
                      background: currentDictamen.evaluada && (currentDictamen.clase === "Añejo" || currentDictamen.clase === "Reposado" || currentDictamen.clase.includes("Vidrio"))
                        ? "linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.14) 100%)"
                        : "linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(217, 119, 6, 0.12) 100%)",
                      border: currentDictamen.evaluada && (currentDictamen.clase === "Añejo" || currentDictamen.clase === "Reposado" || currentDictamen.clase.includes("Vidrio"))
                        ? "1.5px solid #10B981"
                        : "1.5px solid #F59E0B",
                      borderRadius: "12px",
                      padding: "12px 16px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between"
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <VerifiedIcon
                          style={{
                            fontSize: 18,
                            color: currentDictamen.evaluada && (currentDictamen.clase === "Añejo" || currentDictamen.clase === "Reposado" || currentDictamen.clase.includes("Vidrio")) ? "#059669" : "#D97706"
                          }}
                        />
                        <span style={{ fontSize: "10.5px", fontWeight: 800, textTransform: "uppercase", color: "#475569", letterSpacing: "0.4px" }}>
                          Dictamen NOM-070-SCFI-2016
                        </span>
                      </div>
                      <span
                        style={{
                          fontSize: "12px",
                          fontWeight: 900,
                          padding: "3px 10px",
                          borderRadius: "20px",
                          background: currentDictamen.evaluada && (currentDictamen.clase === "Añejo" || currentDictamen.clase === "Reposado" || currentDictamen.clase.includes("Vidrio"))
                            ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
                            : "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
                          color: "#FFF",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                          letterSpacing: "0.3px"
                        }}
                      >
                        {currentDictamen.clase === "Añejo" || currentDictamen.clase === "Reposado" ? "🏆 " : (currentDictamen.clase.includes("Vidrio") ? "🏺 " : "🌱 ")}
                        {currentDictamen.clase}
                      </span>
                    </div>

                    <div style={{ fontSize: "13px", fontWeight: 700, color: "#0F172A", marginTop: "2px" }}>
                      Tiempo transcurrido: <span style={{ color: "#D97706", fontWeight: 800 }}>{currentDictamen.edad}</span>
                    </div>

                    <p style={{ margin: "5px 0 0 0", fontSize: "11.5px", color: "#475569", lineHeight: 1.35 }}>
                      {currentDictamen.mensaje}
                    </p>
                  </div>
                </div>
              </section>

              {/* SECCIÓN 3: VOLÚMENES Y BALANCE DE SALIDA */}
              <section
                style={{
                  background: "#FFFFFF",
                  border: "1px solid #E2E8F0",
                  borderRadius: "14px",
                  padding: "16px 18px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
                }}
              >
                <header style={{ marginBottom: "14px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(5, 150, 105, 0.12)",
                      color: "#059669"
                    }}
                  >
                    <InventoryIcon style={{ fontSize: 19 }} />
                  </div>
                  <div>
                    <h4
                      style={{
                        fontSize: "13.5px",
                        fontWeight: 800,
                        margin: 0,
                        color: "#0F172A",
                        textTransform: "uppercase",
                        letterSpacing: "0.3px"
                      }}
                    >
                      3. Volúmenes y Balance de Extracción
                    </h4>
                    <p style={{ margin: "1px 0 0 0", color: "#64748B", fontSize: "12px" }}>
                      Capture el volumen extraído, merma y graduación alcohólica final
                    </p>
                  </div>
                </header>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(175px, 1fr))", gap: "12px" }}>
                  <div className="integrated-input-box">
                    <label className="integrated-input-label">VOLUMEN A EXTRAER (SALIDA) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={currentSaldoDisponible > 0 ? currentSaldoDisponible : undefined}
                      className="integrated-input-control"
                      value={salidaForm.volumenSalida}
                      onChange={(e) => setSalidaForm({ ...salidaForm, volumenSalida: e.target.value })}
                      required
                      placeholder="0.00"
                      style={{ fontWeight: 800, color: "#059669", fontSize: "14px" }}
                    />
                    <span className="integrated-input-suffix">L</span>
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">MERMA EN MADURACIÓN (L)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="integrated-input-control"
                      value={salidaForm.merma}
                      onChange={(e) => setSalidaForm({ ...salidaForm, merma: e.target.value })}
                      placeholder="0.00"
                      style={{ fontWeight: 700, color: "#D97706" }}
                      title="Pérdida por evaporación o absorción en recipientes"
                    />
                    <span className="integrated-input-suffix">L</span>
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">GRADO ALCOHÓLICO SALIDA *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="35"
                      max="55"
                      className="integrated-input-control"
                      value={salidaForm.salidaAlc}
                      onChange={(e) => setSalidaForm({ ...salidaForm, salidaAlc: e.target.value })}
                      required
                      placeholder="45.00"
                      style={{ fontWeight: 800, color: "#2563EB", fontSize: "14px" }}
                    />
                    <span className="integrated-input-suffix">%</span>
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">RECIPIENTES DESOCUPADOS *</label>
                    <input
                      type="number"
                      min="1"
                      className="integrated-input-control"
                      value={salidaForm.salidaRecipientes}
                      onChange={(e) => setSalidaForm({ ...salidaForm, salidaRecipientes: e.target.value })}
                      required
                      placeholder="1"
                      style={{ fontWeight: 700 }}
                    />
                    <span className="integrated-input-suffix">Pzas</span>
                  </div>
                </div>

                {/* Balance Informativo y Barra de Progreso */}
                <div
                  style={{
                    marginTop: "12px",
                    padding: "12px 16px",
                    background: currentVolSalida + currentMerma > (currentSaldoDisponible || currentVolInicial)
                      ? "rgba(239, 68, 68, 0.06)"
                      : "rgba(248, 250, 252, 0.8)",
                    border: currentVolSalida + currentMerma > (currentSaldoDisponible || currentVolInicial)
                      ? "1px solid rgba(239, 68, 68, 0.3)"
                      : "1px solid #E2E8F0",
                    borderRadius: "10px"
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "#475569" }}>
                      <span>Saldo Inicial: <strong>{currentSaldoDisponible.toFixed(2)} L</strong></span>
                      <span>—</span>
                      <span style={{ color: "#059669" }}>Salida: <strong>-{currentVolSalida.toFixed(2)} L</strong></span>
                      <span>—</span>
                      <span style={{ color: "#D97706" }}>Merma: <strong>-{currentMerma.toFixed(2)} L</strong></span>
                    </div>

                    <div style={{ fontSize: "13px", fontWeight: 800, color: currentSaldoRemanente > 0 ? "#059669" : "#64748B" }}>
                      Saldo remanente: <span style={{ color: currentSaldoRemanente > 0 ? "#059669" : "#0F172A", fontWeight: 900 }}>{currentSaldoRemanente.toFixed(2)} L</span>
                    </div>
                  </div>

                  {/* Progress bar del balance */}
                  {currentSaldoDisponible > 0 && (
                    <div style={{ width: "100%", height: "6px", background: "#E2E8F0", borderRadius: "999px", overflow: "hidden", marginTop: "8px" }}>
                      <div
                        style={{
                          height: "100%",
                          width: `${Math.min(100, ((currentVolSalida + currentMerma) / currentSaldoDisponible) * 100)}%`,
                          background: currentVolSalida + currentMerma > currentSaldoDisponible
                            ? "#EF4444"
                            : "linear-gradient(90deg, #10B981 0%, #D97706 100%)",
                          borderRadius: "999px",
                          transition: "width 0.3s ease"
                        }}
                      />
                    </div>
                  )}

                  {currentVolSalida + currentMerma > (currentSaldoDisponible || currentVolInicial) && (
                    <div style={{ marginTop: "8px", color: "#DC2626", fontSize: "11.5px", fontWeight: 700 }}>
                      ⚠️ La extracción ({currentVolSalida} L) más la merma ({currentMerma} L) superan el saldo disponible ({currentSaldoDisponible} L).
                    </div>
                  )}
                </div>
              </section>

              {/* SECCIÓN 4: DESTINO Y NUEVO LOTE */}
              <section
                style={{
                  background: "#FFFFFF",
                  border: "1px solid #E2E8F0",
                  borderRadius: "14px",
                  padding: "16px 18px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
                }}
              >
                <header style={{ marginBottom: "14px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(124, 58, 237, 0.12)",
                      color: "#7C3AED"
                    }}
                  >
                    <LocalOfferIcon style={{ fontSize: 19 }} />
                  </div>
                  <div>
                    <h4
                      style={{
                        fontSize: "13.5px",
                        fontWeight: 800,
                        margin: 0,
                        color: "#0F172A",
                        textTransform: "uppercase",
                        letterSpacing: "0.3px"
                      }}
                    >
                      4. Identificación del Producto de Salida y Destino
                    </h4>
                    <p style={{ margin: "1px 0 0 0", color: "#64748B", fontSize: "12px" }}>
                      Identificación del nuevo lote resultante que se registrará en Almacén de Graneles
                    </p>
                  </div>
                </header>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div className="integrated-input-box">
                    <label className="integrated-input-label">NUEVO LOTE RESULTANTE (A GRANEL) *</label>
                    <input
                      type="text"
                      className="integrated-input-control"
                      value={salidaForm.nuevoLote}
                      onChange={(e) => setSalidaForm({ ...salidaForm, nuevoLote: e.target.value })}
                      required
                      placeholder="Ej: AÑE-LOTE-01"
                      style={{ fontWeight: 800, color: "#7C3AED", fontSize: "13.5px" }}
                    />
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">DESTINO / CONTENEDOR RECEPTOR *</label>
                    <input
                      type="text"
                      className="integrated-input-control"
                      value={salidaForm.destino}
                      onChange={(e) => setSalidaForm({ ...salidaForm, destino: e.target.value })}
                      placeholder="Ej: Tanque de Granel #2"
                      style={{ fontWeight: 600 }}
                    />
                  </div>
                </div>

                <div
                  className="integrated-input-box"
                  style={{
                    marginTop: "12px",
                    minHeight: "75px",
                    justifyContent: "flex-start",
                    padding: "10px 14px"
                  }}
                >
                  <label className="integrated-input-label">OBSERVACIONES DE SALIDA Y TRASVASE</label>
                  <textarea
                    rows="2"
                    className="integrated-input-control"
                    value={salidaForm.observaciones}
                    onChange={(e) => setSalidaForm({ ...salidaForm, observaciones: e.target.value })}
                    placeholder="Detalles sobre el vaciado, trasvase, estado organoléptico del mezcal tras la maduración, etc."
                    style={{ resize: "vertical", marginTop: "4px", width: "100%" }}
                  />
                </div>
              </section>

              {/* Footer con Botones */}
              <div className="modal-footer-custom" style={{ marginTop: "4px", borderRadius: "10px" }}>
                <button
                  type="button"
                  onClick={() => setIsSalidaModalOpen(false)}
                  className="btn-cancel-modal"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-submit-modal"
                  style={{
                    background: "linear-gradient(135deg, #D97706 0%, #B45309 100%)",
                    boxShadow: "0 4px 14px rgba(217, 119, 6, 0.35)",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                  }}
                >
                  <HourglassFullIcon style={{ fontSize: 18 }} />
                  <span>Registrar Salida de Maduración</span>
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MaduracionSection;
